<template>
  <section>
    <div
      class="row">
      <div
        v-if="!finishLoadingContacts"
        class="col-12 text-center">
        <coozzy-spinner />
      </div>
      <div
        v-else
        :class="email && source === 'email' && !showTickets ? 'col-8' : 'col-12'">
        <form-wizard
          id="wizard"
          ref="wizard"
          :class="showTickets ? 'hideSteps' : ''"
          :hide-buttons="loading"
          title=""
          subtitle=""
          color="rgb(0, 68, 95)"
          step-size="xs">
          <template #title>
            <span />
          </template>
          <tab-content
            v-if="source === 'email'"
            :title="$tc('message.createOrder.selectObject')">
            <order-objects-tab
              v-if="finishLoadingContacts"
              ref="order-recipient-tab"
              :properties="properties"
              :buildings="buildings"
              :objects="objects"
              :tenancies="tenancies"
              :condo-owners="condoOwners"
              :contacts="combinedContacts"
              :preselected-ids="selectedObjects.map(x => x.id)"
              @references-removed="referencesRemoved"
              @loaded-data-algolia="setLoadedData"
              @object-selected="setObjectSelected" />
          </tab-content>
          <tab-content
            :title="$tc('message.createOrder.selectedDevices')">
            <div
              v-if="loading"
              class="text-center">
              <coozzy-spinner />
            </div>
            <order-device-tab
              v-else
              ref="orderDeviceTab"
              :buildings="buildings.concat(buildingsAlgolia)"
              :devices="devices"
              :objects="objects.concat(objectsAlgolia)"
              :properties="properties.concat(propertiesAlgolia)"
              :selected-objects="selectedObjects"
              :rooms="rooms"
              :contacts="combinedContacts"
              :tenancies="tenancies"
              :condo-owners="condoOwners"
              :preselected-devices="effectivePreselectedDevices"
              :devices-for-api="devicesForApi"
              :source="source"
              @device-added="deviceAdded"
              @device-deleted="deviceDeleted"
              @room-added="roomAdded"
              @device-selection-updated="deviceSelectionUpdated"
              @note-changed="noteChanged" />
          </tab-content>
          <tab-content :title="$tc('message.createOrder.selectReceiver')">
            <order-recipient-tab
              v-if="finishLoadingContacts"
              ref="order-recipient-tab"
              :buildings="buildings.concat(buildingsAlgolia)"
              :craftsman-contacts="craftsmanContacts"
              :janitor-contacts="janitorContacts"
              :objects="objects.concat(objectsAlgolia)"
              :service-provider-contacts="serviceProviderContacts"
              :source="source"
              :preselected-contact-id="draftCorrespondenceContactId"
              :preselected-contact-person-id="draftRecipientContactPersonId"
              @recipient-changed="recipientChanged"
              @load-new-added-contacts="loadNewAddedContacts"
              @contact-updated="contactUpdated" />
          </tab-content>
          <tab-content :title="$tc('message.createOrder.title')">
            <div
              v-if="loading"
              class="text-center">
              <coozzy-spinner />
            </div>
            <order-data-tab
              v-if="!loading && employeeContacts && employeeContacts.length > 0"
              v-show="!showTickets"
              ref="orderDataTab"
              :condo-owners="condoOwners.concat(condoOwnersAlgolia)"
              :condo-owners-contact="condoOwnerContacts"
              :craftsman-contacts="craftsmanContacts"
              :employee-contacts="employeeContacts"
              :janitor-contacts="janitorContacts"
              :objects="objects"
              :object-references="objectsAlgolia.length > 0 ? objectsAlgolia : selectedObjects"
              :operator="recipientContact"
              :owners-list="ownersList"
              :references="references"
              :service-provider-contacts="serviceProviderContacts"
              :source="source"
              :tenancies="tenancies.concat(tenanciesAlgolia)"
              :tenant-contacts="tenantContacts"
              :rooms="rooms"
              :ticket-title="ticketTitle"
              :delegate-contacts="delegateContacts"
              :linked-ticket="linkedTicket"
              :description-merge-orders="descriptionMergeOrders"
              :media="mediasList"
              @updated="orderDataUpdated"
              @selected-contract="itemSelected"
              @update-attach-auto="updateAttachAuto"
              @update-status-contact="updateStatusContacts"
              @medias-list-updated="mediasListUpdated"
              @uploading-changed="setAttachmentUploading"
              @contact-updated="contactUpdated" />
            <div
              v-if="!loading"
              v-show="showTickets">
              <template
                v-for="(ticket, index) in listTickets">
                <ticket-details-view
                  :key="'ticket_' + index"
                  :order-ticket="ticket" />
              </template>
            </div>
          </tab-content>
          <template
            #footer="props">
            <template v-if="!loading">
              <div class="wizard-footer-left">
                <wizard-button
                  v-if="props.activeTabIndex === 0"
                  :style="props.fillButtonStyle"
                  @click.native="cancelCreation()">
                  {{ $t('message.generic.cancel') }}
                </wizard-button>
                <wizard-button
                  v-else-if="props.isLastStep && showTickets"
                  :style="props.fillButtonStyle"
                  @click.native="toggleListTickets()">
                  {{ $t('message.generic.form.modify') }}
                </wizard-button>
                <wizard-button
                  v-else
                  :style="props.fillButtonStyle"
                  @click.native="backClicked(props)">
                  {{ $t('message.generic.back') }}
                </wizard-button>
              </div>
              <div class="wizard-footer-right">
                <wizard-button
                  v-if="source === 'email' ? props.activeTabIndex === 3 : props.activeTabIndex === 2"
                  class="mr-2"
                  :disabled="attachmentUploading"
                  :style="props.fillButtonStyle"
                  @click.native="saveDraft">
                  {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
                </wizard-button>
                <wizard-button
                  v-if="!props.isLastStep && (source === 'email' ? props.activeTabIndex === 2 : props.activeTabIndex === 1) && !recipientContactId"
                  :style="props.fillButtonStyle"
                  :disabled="attachmentUploading"
                  class="wizard-footer-right"
                  @click.native="nextStep(props)">
                  <template>
                    {{ $t('message.createOrder.continueWithoutContact') }}
                  </template>
                </wizard-button>
                <wizard-button
                  v-else-if="!props.isLastStep"
                  :style="props.fillButtonStyle"
                  class="wizard-footer-right"
                  @click.native="nextStep(props)">
                  <template>
                    {{ $t('message.generic.next') }}
                  </template>
                </wizard-button>
                <wizard-button
                  v-else-if="props.isLastStep && !hasTicketingEnabled"
                  :disabled="contractTemplateSelected.length === 0 || attachmentUploading"
                  :style="props.fillButtonStyle"
                  class="wizard-footer-right"
                  @click.native="finishProcess">
                  {{ $t('message.createOrder.title') }}
                </wizard-button>
                <template
                  v-else-if="props.isLastStep && hasTicketingEnabled && !showTickets">
                  <coozzy-form-checkbox
                    v-if="selectedDevices.length > 1 && source !== 'mergeOrders'"
                    v-model="createSeparateTicket"
                    :initial="createSeparateTicket"
                    class="float-left pr-3 mt-1 mr-0 display-none-xs font-weight-lighter">
                    {{ $t('message.createSeparateTicket') }}
                  </coozzy-form-checkbox>
                  <wizard-button
                    :style="props.fillButtonStyle"
                    :disabled="attachmentUploading"
                    @click.native="toggleListTickets">
                    {{ $t('message.ticketPreview') }}
                  </wizard-button>
                </template>
                <wizard-button
                  v-else-if="showTickets"
                  :disabled="disableBtns || attachmentUploading"
                  :style="props.fillButtonStyle"
                  @click.native="submitTickets(false)">
                  {{ $t('message.generic.form.send') }}
                </wizard-button>
              </div>
            </template>
          </template>
        </form-wizard>
      </div>
      <div
        v-if="finishLoadingContacts && email && source === 'email' && !showTickets"
        class="col-4 mt-5">
        <print-message
          class=" scrollable-print-message"
          :email="email"
          :medias-list="emailMediasList" />
      </div>
    </div>
    <b-modal
      id="confirmationModal"
      ref="confirmationModal"
      size="lg"
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer
      no-close-on-backdrop>
      <p>{{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}</p>
      <div class="col">
        <coozzy-button
          class="border mb-0"
          design="transparent"
          size="small"
          @click="$bvModal.hide('confirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          class="float-right mb-0"
          design="green"
          size="small"
          @click="confirmCancellation">
          {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>

<script lang="ts">
import Vue from 'vue'
import ObjectApi from '@/misc/apis/ObjectApi'
import ContactApi from '@/misc/apis/ContactApi'
import { obj } from '@/mixins/object'
import { formatting } from '@/mixins/formatting'
import { tables } from '@/mixins/tables'
import { routeChecks } from '@/mixins/routeChecks'
import OrderRecipientTab from '@/order/components/OrderRecipientTab.vue'
import OrderDeviceTab from '@/order/components/OrderDeviceTab.vue'
import OrderDataTab from '@/order/components/OrderDataTab.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import { mapActions, mapGetters } from 'vuex'
import { user } from '@/mixins/user'
import { media } from '@/mixins/media'
import { ticketConnections } from '@/mixins/ticketConnections'
import TicketApi from '@/misc/apis/TicketApi'
import UserApi from '@/misc/apis/UserApi'
import RoomApi from '@/misc/apis/RoomApi'
import TicketDetailsView from '@/management/components/ticket/TicketDetailsView.vue'
import MediaApi from '@/misc/apis/MediaApi'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import MessageApi from '@/misc/apis/MessageApi'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import * as Sentry from '@sentry/browser'
import PrintMessage from '@/contact/components/PrintMessage.vue'
import OrderObjectsTab from '@/order/components/OrderObjectsTab.vue'
import {
  CondominiumCondominium,
  Contractv2ContractTemplate,
  ObjectbuildingBuilding,
  ObjectobjectObject,
  Propertyv2Property,
  ObjectroomRoom,
  ObjecttenancyTenancy,
  ObjectViewingAppointmentContactType,
  TicketTicket,
  TicketTicketLink,
  TypeDate,
  V2Mail
} from '@/types/api'
import { TenancyRecord } from '@/types/search/TenancyRecord'
import { CondominiumRecord } from '@/types/search/CondominiumRecord'
import { Contact, HandoverDevice, UploadedMedia } from '@/types/models'
import { Route } from 'vue-router'

type ContractTemplate = Contractv2ContractTemplate & {
  firstSignature: string
  secondSignature: string
}

export default Vue.extend({
  name: 'CreateOrderView',
  components: {
    OrderObjectsTab,
    PrintMessage,
    CoozzyButton,
    CoozzyFormCheckbox,
    TicketDetailsView,
    CoozzySpinner,
    OrderDataTab,
    OrderDeviceTab,
    OrderRecipientTab
  },
  mixins: [user, formatting, obj, routeChecks, tables, media, ticketConnections],
  beforeRouteEnter(to: Route, from: Route, next) {
    next(vm => {
      (vm as Vue & { prevRoute: Route | null }).prevRoute = from
    })
  },
  beforeRouteLeave(to: Route, _from: Route, next) {
    this.nextToPath = to
    if (!this.confirmCancellationProcess) {
      this.$refs.confirmationModal.show()
    } else {
      next()
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.onBoarding.devices.createOrder')
    }
  },
  props: {
    buildingIds: {
      type: String,
      default: ''
    },
    deviceIds: {
      type: String,
      default: ''
    },
    propertyIds: {
      type: String,
      default: ''
    },
    objectIds: {
      type: String,
      default: ''
    },
    tenancyIds: {
      type: String,
      default: ''
    },
    condoOwnerIds: {
      type: String,
      default: ''
    },
    roomIds: {
      type: String,
      default: ''
    },
    contactIds: {
      type: String,
      default: ''
    },
    mediaIds: {
      type: String,
      default: ''
    },
    source: {
      // values ['property', 'building', 'object', 'condominium', 'device', 'tenancy', 'condoOwner', 'email', 'mergeOrders']
      type: String,
      default: ''
    },
    linkedTicketId: {
      type: String,
      default: ''
    },
    emailsData: {
      type: String,
      default: ''
    },
    ticketIds: {
      type: String,
      default: ''
    },
    mainTicketForMerge: {
      type: String,
      default: ''
    },
    draftTicketId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      recipientContactId: '' as string,
      recipientContact: null as Contact | null,
      devices: [] as HandoverDevice[],
      objects: [] as ObjectobjectObject[],
      buildings: [] as ObjectbuildingBuilding[],
      properties: [] as Propertyv2Property[],
      objectsAlgolia: [] as ObjectobjectObject[],
      tenanciesAlgolia: [] as ObjecttenancyTenancy[],
      condoOwnersAlgolia: [] as CondominiumCondominium[],
      buildingsAlgolia: [] as ObjectbuildingBuilding[],
      propertiesAlgolia: [] as Propertyv2Property[],
      condoOwnerContacts: [] as Contact[],
      tenantContacts: [] as Contact[],
      janitorContacts: [] as Contact[],
      craftsmanContacts: [] as Contact[],
      serviceProviderContacts: [] as Contact[],
      loading: false as boolean,
      selectedEmployee: '' as string,
      onSiteContactType: '' as ObjectViewingAppointmentContactType | string,
      onSiteContactPersonIds: [] as string[],
      contact: {
        firstName: '' as string,
        lastName: '' as string,
        email: '' as string,
        phone: '' as string
      },
      executeFrom: null as TypeDate | null,
      executeUntil: null as TypeDate | null,
      orderDescription: '' as string,
      devicesForApi: [] as HandoverDevice[],
      attachAutomatically: false as boolean,
      contractTemplateSelected: [] as string[],
      contractTemplate: [] as ContractTemplate[],
      hasTicketingEnabled: false as boolean,
      createSeparateTicket: false as boolean,
      listTickets: [] as TicketTicket[],
      showTickets: false as boolean,
      disableBtns: false as boolean,
      attachmentUploading: false as boolean,
      ticketKeys: [] as string[],
      mediasList: [] as UploadedMedia[],
      ownersList: [] as Contact[],
      ownersIds: [] as string[],
      tenancies: [] as ObjecttenancyTenancy[],
      condoOwners: [] as CondominiumCondominium[],
      references: [] as Record<string, unknown>[],
      rooms: [] as ObjectroomRoom[],
      finishLoadingContacts: false as boolean,
      priority: 'DEFAULT' as string,
      selectedContactsUpdatesStatus: [] as Contact[],
      finishLoadingReferences: false as boolean,
      confirmCancellationProcess: false as boolean,
      nextToPath: null as object | null,
      contacts: [] as Contact[],
      ticketTitle: '' as string,
      delegateContacts: [] as Contact[],
      email: null as V2Mail | null,
      selectedDevices: [] as HandoverDevice[],
      selectedObjects: [] as ObjectobjectObject[],
      emailMediasList: [] as UploadedMedia[],
      linkedTicket: null as TicketTicket | null,
      ticketsToMerge: [] as TicketTicket[],
      draftTicket: null as TicketTicket | null
    }
  },
  computed: {
    descriptionMergeOrders() {
      let description = ''
      this.ticketsToMerge.forEach((item, index) => {
        if ((this.mainTicketForMerge === '' || (this.mainTicketForMerge !== '' && this.mainTicketForMerge !== item.id)) && item.description) {
          description += (index > 0 ? '\n\n' : '') + item.description
        }
      })
      return description
    },
    employeeContacts() {
      return (this.$store.getters['employee/getEmployees'] || []).filter(
        e => !e.deleted && !e.blocked
      )
    },
    combinedContacts() {
      return [
        ...this.janitorContacts,
        ...this.craftsmanContacts,
        ...this.serviceProviderContacts,
        ...this.tenantContacts,
        ...this.condoOwnerContacts,
        ...this.delegateContacts
      ]
    },
    prevRoutePath() {
      return this.prevRoute ? this.prevRoute.path : '/'
    },
    prevRouteQuery() {
      return this.prevRoute ? this.prevRoute.query : null
    },
    effectivePreselectedDevices() {
      if (this.deviceIds) return this.deviceIds
      if (this.draftTicket?.references?.length > 0) {
        const ids = this.draftTicket.references
          .filter(ref => ref.id.startsWith('device_'))
          .map(ref => ref.id)
        if (ids.length > 0) return ids.join(',')
      }
      return ''
    },
    draftCorrespondenceContactId() {
      if (this.draftTicket?.correspondences?.length > 0) {
        const companyId = this.draftTicket.additionalData?.find(d => d.key === 'recipientCompanyId')?.value
        return companyId || this.draftTicket.correspondences[0].contactId || ''
      }
      return ''
    },
    draftRecipientContactPersonId() {
      if (this.draftTicket?.correspondences?.length > 0) {
        const companyId = this.draftTicket.additionalData?.find(d => d.key === 'recipientCompanyId')?.value
        if (companyId) {
          return this.draftTicket.correspondences[0].contactId || ''
        }
      }
      return ''
    }
  },
  watch: {
    devices: function (newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        const existingNotes = {}
        this.devicesForApi.forEach(device => {
          if (device.note) {
            existingNotes[device.id] = device.note
          }
        })
        this.devicesForApi = []
        for (let i = 0; i < this.devices.length; i++) {
          this.devicesForApi.push({
            id: this.devices[i].id,
            note: existingNotes[this.devices[i].id] || ''
          })
        }
      }
    },
    finishLoadingReferences: function (newVal) {
      if (newVal === true) {
        this.loadOwners()
        this.loadJanitors()
        this.loadCraftsmans()
        this.loadServiceProviders()
        this.loadDelegates()
        this.$nextTick(() => {
          this.finishLoadingContacts = true
        })
      }
    }
  },
  mounted() {
    this.loadData()
    TicketApi.hasTicketingEnabled(this.accountId)
      .then(response => {
        this.hasTicketingEnabled = response.enabled
        if (!this.draftTicketId) {
          this.getReservedKeys(1)
        }
      })
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    setAttachmentUploading(isUploading: boolean) {
      this.attachmentUploading = isUploading === true
    },
    async loadNewAddedContacts(contactIds: string[] = []) {
      const hasNewContacts = Array.isArray(contactIds) && contactIds.length > 0
      if (!hasNewContacts) {
        return
      }
      try {
        const buildingIds = this.buildings.map(building => building.id).filter(Boolean)
        let refreshedBuildings = []
        if (buildingIds.length > 0) {
          const response = await ObjectApi.getBuildingsListByIds(buildingIds)
          refreshedBuildings = response.buildings
        } else if (this.objects.length > 0) {
          const objectBuildingIds = [...new Set(this.objects.map(obj => obj.buildingId).filter(Boolean))]
          if (objectBuildingIds.length > 0) {
            const response = await ObjectApi.getBuildingsListByIds(objectBuildingIds)
            refreshedBuildings = response.buildings
          }
        }
        if (refreshedBuildings.length > 0) {
          const list = refreshedBuildings.concat(this.buildings)
          this.buildings = list.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
          this.ownersIds = this.removeDuplicatedIds(this.ownersIds.concat(refreshedBuildings.map(x => x.ownerId)))
        }
        await this.loadCraftsmans()
        await this.loadServiceProviders()
        await this.loadJanitors()
        await this.loadDelegates()
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    normalizeIds(ids: string | string[] | undefined | null): string[] {
      if (!ids) {
        return []
      }
      const values = Array.isArray(ids) ? ids : ids.split(',')
      return [...new Set(values.map(id => id?.trim()).filter(Boolean))]
    },
    async loadData() {
      try {
        if (this.draftTicketId) {
          this.loading = true
        }
        const objectIds = this.normalizeIds(this.objectIds)
        const buildingIds = this.normalizeIds(this.buildingIds)
        const propertyIds = this.normalizeIds(this.propertyIds)
        const tenancyIds = this.normalizeIds(this.tenancyIds)
        const condoOwnerIds = this.normalizeIds(this.condoOwnerIds)
        const roomIds = this.normalizeIds(this.roomIds)
        const deviceIds = this.normalizeIds(this.deviceIds)
        const isDraft = !!this.draftTicketId
        const sourceMatchesRef = ['ticket', 'mergeOrders', 'draftOrder'].includes(this.source) || isDraft
        const scopedDraftSource = (isDraft || this.source === 'draftOrder') && this.source !== 'mergeOrders'
        const scopedReferenceType = scopedDraftSource
          ? (objectIds.length > 0 ? 'object' : buildingIds.length > 0 ? 'building' : propertyIds.length > 0 ? 'property' : '')
          : ''
        if (this.source === 'device' || (sourceMatchesRef && deviceIds.length > 0)) {
          await this.loadDevices()
        }
        if (this.source === 'object' || this.source === 'condominium' || (sourceMatchesRef && objectIds.length > 0 && (scopedReferenceType === '' || scopedReferenceType === 'object'))) {
          await this.loadObjects(objectIds)
          if (this.source !== 'mergeOrders') {
            await this.getDevicesByRefId(objectIds)
            this.selectedObjects = this.objects.filter(x => objectIds.includes(x.id))
            await this.loadRoomsByReference(objectIds)
          }
        }
        if (this.source === 'building' || (sourceMatchesRef && buildingIds.length > 0 && (scopedReferenceType === '' || scopedReferenceType === 'building'))) {
          await this.loadBuildings(buildingIds, [])
          if (this.source !== 'mergeOrders') {
            await this.getDevicesByRefId(buildingIds)
            this.selectedObjects = this.buildings.filter(x => buildingIds.includes(x.id))
            await this.loadRoomsByReference(buildingIds)
          }
        }
        if (this.source === 'property' || (sourceMatchesRef && propertyIds.length > 0 && (scopedReferenceType === '' || scopedReferenceType === 'property'))) {
          await this.loadProperties(propertyIds)
          if (this.source !== 'mergeOrders') {
            await this.getDevicesByRefId(propertyIds)
            this.selectedObjects = this.properties.filter(x => propertyIds.includes(x.id))
          }
        }
        if (this.source === 'tenancy' || (sourceMatchesRef && tenancyIds.length > 0)) {
          await this.loadTenancies(tenancyIds)
        }
        if (this.source === 'condoOwner' || (sourceMatchesRef && condoOwnerIds.length > 0)) {
          await this.loadCondoOwners(condoOwnerIds)
        }
        if (this.source === 'room' || (sourceMatchesRef && roomIds.length > 0)) {
          await this.loadRooms(roomIds)
        }
        if (sourceMatchesRef && this.contactIds) {
          await this.loadContacts()
        }
        if (this.source === 'email' && this.contactIds) {
          await this.loadContactsAssets()
        }
        if (this.source === 'email') {
          const emailId = JSON.parse(this.emailsData)?.id
          await MessageApi.listMailsByIds([emailId]).then(response => {
            if (response.mails?.length > 0) {
              this.email = response.mails[0]
              if (this.email.attachments?.length) {
                for (let index = 0; index < this.email.attachments.length; index++) {
                  MediaApi.getMediaByID(this.email.attachments[index].mediaId)
                    .then(response => {
                      this.emailMediasList.push(response.media)
                    })
                    .catch(() => {
                      Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
                    })
                }
              } else if (this.email.attachmentMediaIds?.length) {
                MediaApi.getListMedia(this.email.attachmentMediaIds)
                  .then(response => {
                    for (const media of response.media) {
                      this.emailMediasList.push(media)
                    }
                  })
                  .catch(() => {
                    Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
                  })
              } else {
                this.emailMediasList = []
              }
            }
          })
        }
        if (this.source === 'ticket' && this.linkedTicketId !== '') {
          await TicketApi.getTicketByIds([this.linkedTicketId]).then(response => {
            if (response.tickets.length > 0) {
              this.linkedTicket = response.tickets[0]
            }
          })
        }
        if (this.draftTicketId) {
          await TicketApi.getTicketByIds([this.draftTicketId]).then(response => {
            if (response.tickets?.length > 0) {
              this.draftTicket = response.tickets[0]
              this.linkedTicket = this.draftTicket
              if (this.draftTicket.correspondences?.length > 0 && this.draftTicket.correspondences[0].contactId) {
                const corr = this.draftTicket.correspondences[0]
                this.recipientContactId = corr.contactId
                this.recipientContact = {
                  id: corr.contactId,
                  objectID: corr.contactId,
                  name: corr.name || '',
                  email: corr.email || '',
                  address: corr.address || {},
                  companyName: corr.companyName || '',
                  phone: corr.phone || '',
                  phoneNumbers: []
                }
              }
              if (this.draftTicket.key) {
                this.ticketKeys = [this.draftTicket.key]
              }
              if (this.draftTicket.mediaIds?.length > 0) {
                MediaApi.getListMedia(this.draftTicket.mediaIds)
                  .then(mediaResponse => {
                    for (const media of mediaResponse.media) {
                      this.mediasList.push(media)
                    }
                  })
                  .catch(() => {
                    Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
                  })
              }
              this.loadDeviceOrderNotesFromDraft(this.draftTicket)
            }
          })
        }
        if (this.source === 'mergeOrders' && this.ticketIds) {
          const ticketIds = this.ticketIds.split(',')
          if (this.mainTicketForMerge) {
            ticketIds.push(this.mainTicketForMerge)
          }
          const responseTickets = await TicketApi.getTicketByIds(ticketIds)
          this.ticketsToMerge = responseTickets.tickets
          this.ticketsToMerge.forEach(ticket => {
            if (ticket.mediaIds?.length > 0) {
              for (let index = 0; index < ticket.mediaIds.length; index++) {
                MediaApi.getMediaByID(ticket.mediaIds[index])
                  .then(response => {
                    this.mediasList.push(response.media)
                  })
                  .catch(() => {
                    Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
                  })
              }
            }
          })
        }
      } finally {
        if (this.source !== 'email' || this.draftTicketId) {
          await this.loadReferences()
        } else {
          this.finishLoadingReferences = true
        }
        await this.loadCraftsmans()
        await this.loadServiceProviders()
        await this.loadJanitors()
        if (this.draftTicketId) {
          this.syncDevicesForApi()
          this.restoreDraftDeviceNotes()
          this.loading = false
        }
      }
    },
    async loadContactsAssets() {
      const contactIds = this.contactIds.split(',')
      let promises = []
      contactIds.forEach(contactId => {
        promises.push(ObjectApi.getTenancyListByContactId(contactId, true, true, true, true, true))
        promises.push(ObjectApi.getCondominiumListById(contactId, true, true, true, true, true))
      })
      Promise.all(promises).then(async (response) => {
        response.forEach(element => {
          if (element.condominiums) {
            this.condoOwners = this.condoOwners.concat(element.condominiums)
          } else {
            this.tenancies = this.tenancies.concat(element.tenancies)
          }
        })
      // remove duplicated items
        this.condoOwners = this.getCurrentCondominiums()
        this.tenancies = this.getCurrentTenancies()
        const condoOwnerContactIds = this.condoOwners.flatMap(c => [
          ...(c.owner?.contactIds || []),
          ...(c.owner?.roommateContactIds || []),
          c?.owner.billingContactId,
          c?.owner.correspondenceContactId
        ].filter(Boolean))
        const tenantContactIds = this.tenancies.flatMap(t => [
          ...(t.tenant?.contactIds || []),
          ...(t.tenant?.guarantorContactIds || []),
          t?.tenant.billingContactId,
          t?.tenant.correspondenceContactId
        ].filter(Boolean))
        const contactIds = [...condoOwnerContactIds, ...tenantContactIds]
        await this.loadContacts(contactIds)
        const objectIds = this.tenancies.concat(this.condoOwners).map(x => x.objectId) || []
        await this.loadObjects(objectIds)
        const buildingIds = this.objects.map(x => x.buildingId) || []
        const buildingResponse = await ObjectApi.getBuildingsListByIds(buildingIds)
        this.buildings = buildingResponse.buildings
        const propResponse = await ObjectApi.getPropertiesByBuildingIds(buildingIds)
        this.properties = propResponse.properties
        const roomsResponse = await RoomApi.getRoomsByReferenceIds(objectIds.concat(buildingIds))
        this.rooms = roomsResponse.rooms
        this.finishLoadingReferences = true
      })
    },
    getCurrentTenancies() {
      const startOfDayTimestamp = this.getStartOfDayTimestamp()
      const list = this.filterDuplicateItemsById(this.tenancies)
      return list.filter(t => t.startingAt && new Date(this.$options.filters.objectToDateInput(t.startingAt)).getTime() / 1000 <= startOfDayTimestamp && (!t.endingAt || (new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 < 0 || new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 >= startOfDayTimestamp)))
    },
    getCurrentCondominiums() {
      const startOfDayTimestamp = this.getStartOfDayTimestamp()
      const list = this.filterDuplicateItemsById(this.condoOwners)
      return list.filter(t => t.startingAt && new Date(this.$options.filters.objectToDateInput(t.startingAt)).getTime() / 1000 <= startOfDayTimestamp && (!t.endingAt || (new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 < 0 || new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 >= startOfDayTimestamp)))
    },
    getStartOfDayTimestamp() {
      // Because of timezone offset we need to so some trickery here. This only works for timezones with a positive offset
      const now = new Date()
      const localDay = now.getDate()
      now.setUTCHours(0, 0, 0, 0)
      let startOfDayTimestamp
      if (now.getDate() !== localDay) {
        startOfDayTimestamp = Math.trunc((now.getTime() + 86400000) / 1000)
      } else {
        startOfDayTimestamp = Math.trunc(now.getTime() / 1000)
      }

      return startOfDayTimestamp
    },
    setObjectSelected(listObjects: ObjectobjectObject[]) {
      this.selectedObjects = listObjects
      const ids = listObjects.map(x => x.id)
      this.getDevicesByRefId(ids)
      if (listObjects.length > 0) {
        const item = listObjects[0]
        this.loadAdditionalTenants(item)
      }
    },
    async loadAdditionalTenants(item: Propertyv2Property | ObjectbuildingBuilding | ObjectobjectObject) {
      if (item.id.startsWith('prop_')) {
        const property = item as Propertyv2Property
        const buildingIds = property.buildingIds
        let objectIds = []
        buildingIds.forEach(element => {
          const building = this.buildings.find(x => x.id === element)
          if (building) {
            objectIds = objectIds.concat(building.objectIds)
          }
        })
        if (property.type === 'PROPERTY_TYPE_RENTAL') {
          await this.loadTenantContacts(objectIds)
        } else {
          await this.loadCondominiumContacts(objectIds)
        }
      } else if (item.id.startsWith('building_')) {
        const selectedBuilding = item as ObjectbuildingBuilding
        let property = this.properties.find(x => x.buildingIds.includes(selectedBuilding.id))
        const building = this.buildings.find(x => x.id === selectedBuilding.id)
        if (!property) {
          const rspProp = await ObjectApi.getPropertiesByBuildingIds([selectedBuilding.id])
          if (rspProp.properties.length > 0) {
            if (!this.properties.some(x => x.id === rspProp.properties[0].id)) {
              this.properties.push(rspProp.properties[0])
              property = rspProp.properties[0]
            }
          }
        }
        if (property && building) {
          if (property.type === 'PROPERTY_TYPE_RENTAL') {
            await this.loadTenantContacts(building.objectIds)
          } else {
            await this.loadCondominiumContacts(building.objectIds)
          }
        }
      } else if (item.id.startsWith('obj_')) {
        const object = item as ObjectobjectObject
        if (object.condominiumOwnership) {
          await this.loadCondominiumContacts([object.id])
        } else {
          await this.loadTenantContacts([object.id])
        }
      }
    },
    async getDevicesByRefId(ids: string[]) {
      const deviceResponse = await ObjectApi.getDevicesByReferenceIds(ids)
      const list = [...this.devices, ...deviceResponse.devices]
      this.devices = this.filterDuplicateItemsById(list)
      if (this.source !== 'room') {
        let roomIds = this.devices.map(x => x.roomId).filter(Boolean)
        roomIds = [...new Set(roomIds)]
        await this.loadRooms(roomIds)
      }
    },
    deviceAdded(device: HandoverDevice) {
      const oldDevices = [...this.devices]
      const index = oldDevices.findIndex(x => x.id === device.id)
      if (index === -1) {
        oldDevices.unshift(device)
      } else {
        oldDevices.splice(index, 1, device)
      }
      Vue.set(this, 'devices', oldDevices)
    },
    deviceDeleted(device: HandoverDevice) {
      const matchesDevice = (entry: HandoverDevice) =>
        (device.id && entry.id === device.id) ||
        (device.internalId && entry.internalId === device.internalId)
      const removeDeviceFromList = () => {
        const updatedDevices = this.devices.filter(entry => !matchesDevice(entry))
        if (updatedDevices.length !== this.devices.length) {
          Vue.set(this, 'devices', updatedDevices)
        }
      }
      if (device.id) {
        ObjectApi.deleteDevice(device.id).then(() => {
          removeDeviceFromList()
          this.selectedDevices = this.selectedDevices.filter(entry => !matchesDevice(entry))
          Vue.toasted.show(this.$t('message.successMessages.deviceDeleted'), { type: 'success' })
        }).catch(() => {
          Vue.toasted.show(this.$tc('message.deleteErrors.device'), { type: 'error' })
        })
      } else if (device.internalId) {
        removeDeviceFromList()
        this.selectedDevices = this.selectedDevices.filter(entry => !matchesDevice(entry))
      }
    },
    roomAdded(room: ObjectroomRoom) {
      const index = this.rooms.findIndex(x => x.id === room.id)
      if (index === -1) {
        this.rooms.unshift(room)
      } else {
        this.rooms.splice(index, 1, room)
      }
    },
    deviceSelectionUpdated(devices: HandoverDevice[]) {
      this.selectedDevices = devices
      if (this.draftTicketId) {
        this.restoreDraftDeviceNotes()
      }
    },
    referencesRemoved() {
      this.tenanciesAlgolia = []
      this.condoOwnersAlgolia = []
      this.objectsAlgolia = []
      this.buildingsAlgolia = []
      this.propertiesAlgolia = []
    },
    async setLoadedData(data: { tenancy?: TenancyRecord; condominium?: CondominiumRecord; object?: ObjectobjectObject; building?: ObjectbuildingBuilding; property?: Propertyv2Property }) {
      console.log(data)
      if (data.tenancy) {
        this.tenanciesAlgolia.push(data.tenancy)
        this.loadTenantContacts([data.tenancy.obj.id])
      }
      if (data.condominium) {
        this.condoOwnersAlgolia.push(data.condominium)
        this.loadCondominiumContacts([data.condominium.obj.id])
      }
      if (data.object) {
        this.objectsAlgolia.push(data.object)
      }
      if (data.building) {
        this.buildingsAlgolia.push(data.building)
      }
      if (data.property) {
        this.propertiesAlgolia.push(data.property)
      }
      if (this.source === 'email' && (data.property || data.building || data.object)) {
        await this.loadEmailConnectionData(data)
      }
    },
    async loadEmailConnectionData(data: { property?: Propertyv2Property; building?: ObjectbuildingBuilding; object?: ObjectobjectObject }) {
      try {
        if (data.property && !data.building) {
          const buildingIds = data.property.buildingIds
          if (buildingIds.length > 0) {
            const buildingsResponse = await ObjectApi.getBuildingsListByIds(buildingIds)
            this.buildingsAlgolia = this.buildingsAlgolia.concat(buildingsResponse.buildings)
            const objectIds = buildingsResponse.buildings.flatMap(b => b.objectIds)
            if (objectIds.length > 0) {
              const objectsResponse = await ObjectApi.listObjectsByIds(objectIds)
              this.objectsAlgolia = this.objectsAlgolia.concat(objectsResponse.objects)
            }
          }
        }
        await this.loadCraftsmans()
        await this.loadServiceProviders()
        await this.loadJanitors()
        await this.loadDelegates()
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    confirmCancellation() {
      this.confirmCancellationProcess = true
      if (this.nextToPath !== null) {
        this.$router.push({
          name: this.nextToPath.name,
          params: this.nextToPath.params,
          query: this.nextToPath.query
        }, () => {
        })
      } else {
        this.$router.push({
          path: this.prevRoutePath,
          query: this.prevRouteQuery
        }, () => {
        })
      }
    },
    contactUpdated(contact: Contact) {
      if (this.craftsmanContacts.findIndex(element => element.id === contact.id) !== -1) {
        this.loadCraftsmans()
      }
      if (this.serviceProviderContacts.findIndex(element => element.id === contact.id) !== -1) {
        this.loadServiceProviders()
      }
      if (this.janitorContacts.findIndex(element => element.id === contact.id) !== -1) {
        this.loadJanitors()
      }
      if (this.tenantContacts.findIndex(element => element.id === contact.id) !== -1) {
        const objectIds = this.objects.filter(x => x.condominiumOwnership === false).map(x => x.id)
        this.loadTenantContacts(objectIds)
      }
      if (this.condoOwnerContacts.findIndex(element => element.id === contact.id) !== -1) {
        const objectIds = this.objects.filter(x => x.condominiumOwnership === true).map(x => x.id)
        this.loadCondominiumContacts(objectIds)
      }
    },
    getReservedKeys(amount: number) {
      TicketApi.reserveKeys(this.accountId, amount).then(response => {
        this.ticketKeys = this.ticketKeys.concat(response.keys.reverse())
      })
    },
    cancelCreation() {
      if (this.listTickets.length > 0 && this.listTickets[0].listAttachment.length > 0) {
        const ids = this.listTickets[0].listAttachment.map(x => x.renderId)
        ObjectApi.deleteRenderFiles(ids).then(() => {
          this.$router.push({ path: this.prevRoutePath, query: this.prevRouteQuery })
        })
          .catch(exeption => {
            console.error(exeption)
          })
      } else {
        this.$router.push({ path: this.prevRoutePath, query: this.prevRouteQuery })
      }
    },
    toggleListTickets() {
      if (!this.$refs.orderDataTab?.isInvalid()) {
        this.showTickets = !this.showTickets
        if (this.showTickets) {
          this.createTickets()
        }
      }
    },
    filterDuplicateItemsById(items: { id: string }[]) {
      return items.filter((item, index, inputArray) => {
        const current = inputArray[index]
        return inputArray.findIndex(function (i) {
          return i.id === current.id
        }) === index
      })
    },
    mediasListUpdated(list: UploadedMedia[]) {
      this.mediasList = this.filterDuplicateItemsById(list)
    },
    saveDraft() {
      if (!this.$refs.orderDataTab?.isInvalid(false)) {
        this.createTickets(true)
      }
    },
    async createTickets(draft: boolean = false) {
      const promises = []
      const employee = this.employeeContacts.find(x => x.id === this.selectedEmployee)
      const user = this.employeeContacts.find(x => x.id === this.userId)
      this.listTickets = []
      let contactPersonOnSite = null
      if (this.onSiteContactPersonIds.length > 0 && this.onSiteContactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE') {
        contactPersonOnSite = this.employeeContacts.find(x => this.onSiteContactPersonIds.includes(x.id))
        promises.push(ContactApi.resolveByMail(this.accountId, [contactPersonOnSite.email]))
      }
      if (employee) {
        promises.push(ContactApi.resolveByMail(employee.accountId, [employee.email]))
      }
      if (user) {
        promises.push(ContactApi.resolveByMail(user.accountId, [user.email]))
      }
      // check if the number of keys reserved equal to nbr of tickets
      if (this.createSeparateTicket && this.devices.length > 1 && this.ticketKeys.length < this.devices.length) {
        this.getReservedKeys(this.devices.length - 1)
      }
      if (this.orderDescription.includes('#anrede#') || this.orderDescription.includes('#eigentuemer#') || this.orderDescription.includes('#eigentuemer.addresse#')) {
        this.orderDescription = await this.translatePlaceholders(this.orderDescription)
      }
      // this.devices.forEach(element => {
      //   if (element.referenceId.startsWith('obj')) {
      //     promises.push(ObjectApi.listObjectsByIds([element.referenceId]))
      //   } else if (element.referenceId.startsWith('building')) {
      //     promises.push(ObjectApi.getBuildingsListByIds([element.referenceId]))
      //   } else if (element.referenceId.startsWith('prop')) {
      //     promises.push(ObjectApi.getPropertyListByIds([element.referenceId]))
      //   }
      //   if (element.roomId !== '') {
      //     promises.push(RoomApi.getRoomsByReferenceIds([element.referenceId]))
      //   }
      // })

      promises.push(UserApi.listAccountsByIds([this.accountId]))

      const array = []
      const listRenderIds = []
      let listTemplatePdf = this.mediasList.length > 0 ? this.mediasList.map(x => x.id) : []
      if (this.source === 'email') {
        listTemplatePdf = listTemplatePdf.concat(this.mediaIds.split(','))
      }
      let endProcess = false
      if (this.createSeparateTicket) {
        this.selectedDevices.forEach((device, index) => {
          const promisesTemplates = []
          for (let j = 0; j < this.contractTemplateSelected.length; j++) {
            array.push({
              contract: this.contractTemplateSelected[j]
            })
            const signatories = []
            const contract = this.contractTemplate.find(x => x.id === this.contractTemplateSelected[j])
            if (contract && !contract.mediaId.includes('.pdf')) {
              if (contract && contract.firstSignature !== '') {
                signatories.push(contract.firstSignature)
              }
              if (contract && contract.secondSignature) {
                if (contract && contract.firstSignature === '') {
                  signatories.push('')
                }
                signatories.push(contract.secondSignature)
              }
              const deviceForApi = this.devicesForApi.find(x => x.id === device.id)
              promisesTemplates.push(
                ObjectApi.renderDevice(
                  this.contractTemplateSelected[j],
                  this.selectedEmployee,
                  this.recipientContactId,
                  [deviceForApi],
                  this.executeFrom,
                  this.executeUntil,
                  this.orderDescription,
                  this.onSiteContactPersonIds.length > 0 ? this.onSiteContactPersonIds[0] : this.$refs.orderDataTab.selectedUser,
                  null,
                  this.attachAutomatically,
                  signatories,
                  true,
                  this.ticketKeys[index]
                )
              )
            } else if (contract) {
              listTemplatePdf.push(contract.mediaId)
            }
          }
          Promise.all(promisesTemplates).then(response => {
            response.forEach(element => {
              const obj = {
                renderId: element.renderId,
                deviceId: device.id
              }
              listRenderIds.push(obj)
            })
          }).finally(() => {
            if (index === this.selectedDevices.length - 1) {
              endProcess = true
            }
          })
        })
      } else {
        for (let j = 0; j < this.contractTemplateSelected.length; j++) {
          array.push({
            contract: this.contractTemplateSelected[j]
          })
          const signatories = []
          const contract = this.contractTemplate.find(x => x.id === this.contractTemplateSelected[j])
          if (contract && !contract.mediaId.includes('.pdf')) {
            if (contract && contract.firstSignature !== '') {
              signatories.push(contract.firstSignature)
            }
            if (contract && contract.secondSignature) {
              if (contract && contract.firstSignature === '') {
                signatories.push('')
              }
              signatories.push(contract.secondSignature)
            }
            promises.push(
              ObjectApi.renderDevice(
                this.contractTemplateSelected[j],
                this.selectedEmployee,
                this.recipientContactId,
                this.devicesForApi,
                this.executeFrom,
                this.executeUntil,
                this.orderDescription,
                this.onSiteContactPersonIds.length > 0 ? this.onSiteContactPersonIds[0] : this.$refs.orderDataTab.selectedUser,
                null,
                this.attachAutomatically,
                signatories,
                true,
                this.ticketKeys[0]
              )
            )
          } else if (contract) {
            listTemplatePdf.push(contract.mediaId)
          }
        }
      }
      Promise.all(promises).then(response => {
        let currentContactPersonOnSite = []
        let account = null
        let contacts = []
        const correspondences = []
        const renderIds = []
        let docList = []
        response.forEach(element => {
          if (element.persons?.length > 0 || element.companies?.length > 0) {
            contacts = contacts.concat(element.companies).concat(element.persons)
          } else if (element.accounts && element.accounts.length > 0) {
            account = element.accounts[0]
          } else if (element.renderId) {
            renderIds.push(element.renderId)
          }
        })

        if (this.onSiteContactPersonIds.length > 0 && this.onSiteContactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE') {
          const user = contacts.find(x => x.ownerId === contactPersonOnSite.accountId && x.email === contactPersonOnSite.email) || null
          if (user) {
            currentContactPersonOnSite.push({
              contactId: user.id,
              companyName: '',
              name: user.name || '',
              address: this.convertAddress(user.address) !== '' ? user.address : '',
              email: user.email !== '' ? user.email : '',
              phoneNumbers: user.phoneNumbers.length > 0 ? user.phoneNumbers : '',
              notify: false,
              notifyOnlyStatusChanges: false,
              includeCorrespondenceContact: false
            })
          }
        }

        if (this.onSiteContactPersonIds.length > 0) {
          this.onSiteContactPersonIds.forEach(element => {
            const contact = this.combinedContacts.find(x => x.id === element || x.contactPersons?.some(item => item.id === element))
            if (contact && contact.id === element) {
              currentContactPersonOnSite.push({
                contactId: contact.id,
                companyName: '',
                name: contact.name || '',
                address: this.convertAddress(contact.address) !== '' ? contact.address : '',
                email: contact.email !== '' ? contact.email : '',
                phoneNumbers: contact.phoneNumbers.length > 0 ? contact.phoneNumbers : '',
                notify: false,
                notifyOnlyStatusChanges: false,
                includeCorrespondenceContact: false
              })
            } else if (contact && contact.contactPersons?.some(item => item.id === element)) {
              const contactPersonSiteInfo = contact.contactPersons.find(x => x.id === element)
              const contactPersonSiteCompany = contact
              currentContactPersonOnSite.push({
                contactId: contactPersonSiteInfo.id,
                companyName: contactPersonSiteCompany ? contactPersonSiteCompany.name : '',
                name: contactPersonSiteInfo.name || '',
                address: this.convertAddress(contactPersonSiteInfo.address) !== '' ? contactPersonSiteInfo.address : contactPersonSiteCompany ? contactPersonSiteCompany.address : '',
                email: contactPersonSiteInfo.email !== '' ? contactPersonSiteInfo.email : contactPersonSiteCompany ? contactPersonSiteCompany.email : '',
                phoneNumbers: contactPersonSiteInfo.phoneNumbers.length > 0 ? contactPersonSiteInfo.phoneNumbers : contactPersonSiteCompany && contactPersonSiteCompany.phoneNumbers.length > 0 ? contactPersonSiteCompany.phoneNumbers : '',
                notify: false,
                notifyOnlyStatusChanges: false,
                includeCorrespondenceContact: false
              })
            }
          })
        }
        let recipientCompanyId = ''
        if (this.recipientContactId !== '') {
          const recipientTab = this.$refs['order-recipient-tab']
          let recipientContact = recipientTab?.recipientContact || recipientTab?.recipientContactfromApi
          let company = null
          if (recipientContact) {
            const recipientId = 'objectID' in recipientContact ? recipientContact.objectID : recipientContact.id
            if (recipientId !== this.recipientContactId && recipientContact.contactPersons?.length > 0) {
              recipientContact = recipientContact.contactPersons.find(x => x.id === this.recipientContactId)
              company = (recipientTab?.recipientContactfromApi || recipientTab?.recipientContact)
              if (company) {
                recipientCompanyId = ('objectID' in company ? company.objectID : company.id) || ''
              }
            }
            if (recipientContact) {
              correspondences.push({
                contactId: this.recipientContactId,
                companyName: company ? company.name : '',
                name: recipientContact.name || '',
                address: this.convertAddress(recipientContact.address) !== '' ? recipientContact.address : company ? company.address : '',
                email: recipientContact.email,
                phoneNumbers: recipientContact.phoneNumbers?.length > 0 ? recipientContact.phoneNumbers : company && company.phoneNumbers?.length > 0 ? company.phoneNumbers : ''
              })
            }
          } else if (this.draftTicket?.correspondences?.length > 0) {
            const corr = this.draftTicket.correspondences.find(c => c.contactId === this.recipientContactId) || this.draftTicket.correspondences[0]
            correspondences.push({
              contactId: corr.contactId,
              companyName: corr.companyName || '',
              name: corr.name || '',
              address: corr.address || {},
              email: corr.email || '',
              phoneNumbers: corr.phone ? [{ number: corr.phone, type: 'WORK' }] : []
            })
          }
        }
        const assigneContact = employee
          ? contacts.find(x => x.ownerId === employee.accountId && x.email === employee.email)
          : null
        const currentAssigne = employee
          ? {
            accountId: this.accountId,
            accountName: account.company ? account.company.name : account.individual ? (account.individual.firstName + ' ' + account.individual.lastName) : '',
            name: employee.profile.firstName + ' ' + employee.profile.lastName,
            profilePictureUrl: employee.profile.pictureUrl,
            userId: employee.id,
            address: account.company ? account.company.address : assigneContact?.address,
            email: employee.email,
            phone: employee.profile.phoneNumber
          }
          : null
        const creatorContact = contacts.find(x => x.ownerId === user.accountId && x.email === user.email)
        const currentUser = {
          accountId: this.accountId,
          accountName: account.company ? account.company.name : account.individual ? (account.individual.firstName + ' ' + account.individual.lastName) : '',
          name: user.profile.firstName + ' ' + user.profile.lastName,
          profilePictureUrl: user.profile.pictureUrl,
          userId: user.id,
          address: account.company ? account.company.address : creatorContact.address,
          email: user.email,
          phone: user.profile.phoneNumber
        }
        const externalTicketWatchers = []
        if (this.$refs?.orderDataTab?.sendStatusUpdates && this.selectedContactsUpdatesStatus.length > 0) {
          this.selectedContactsUpdatesStatus.forEach(contact => {
            if (contact.selectedContactPerson !== null) {
              const contactPerson = contact.contactPersons.find(x => x.id === contact.selectedContactPerson)
              externalTicketWatchers.push({
                name: contactPerson.name || '',
                contactId: contactPerson.id,
                email: contactPerson.email,
                includeCorrespondenceContact: this.$refs?.orderDataTab.includeCorrespondenceContact || false,
                notifyOnlyStatusChanges: true
              })
            } else {
              externalTicketWatchers.push({
                name: contact.name || '',
                contactId: contact.id,
                email: contact.email,
                includeCorrespondenceContact: this.$refs?.orderDataTab.includeCorrespondenceContact || false,
                notifyOnlyStatusChanges: true
              })
            }
          })
        }
        let link = []
        if (this.linkedTicketId !== '' || (this.source === 'mergeOrders' && this.mainTicketForMerge !== '')) {
          link = [{
            ticketId: this.linkedTicketId !== '' ? this.linkedTicketId : this.mainTicketForMerge,
            type: 'TICKET_LINK_TYPE_RELATES'
          }]
        }
        const draftCosts = this.draftTicketId && this.draftTicket
          ? { costs: this.draftTicket.costs, costsEntries: this.draftTicket.costsEntries }
          : {}
        if (this.createSeparateTicket) {
          const checkEndRenderDevices = setInterval(() => {
            if (endProcess) {
              clearInterval(checkEndRenderDevices)
              this.selectedDevices.forEach((element, index) => {
                docList = []
                const description = this.getTicketDescription(element.id) || ''
                const title = this.getTicketTitle(element.id)
                if (listRenderIds.length > 0) {
                  listRenderIds.forEach((elem, index) => {
                    if (elem.deviceId === element.id) {
                      const template = this.contractTemplate.find(x => x.id === array[index].contract)
                      const device = this.selectedDevices.find(x => x.id === elem.deviceId)
                      const contractName = template.title
                      const downloadName = contractName + (device ? ('_' + this.$t('message.onBoarding.devices.typeValues.' + device.type).toString()) : '')

                      const item = {
                        id: Math.random().toString(36).substr(2, 9),
                        filename: downloadName + '.pdf',
                        mimeType: 'application/pdf',
                        renderId: elem.renderId,
                        url: '',
                        loading: false,
                        blob: null
                      }
                      docList.push(item)
                    }
                  })
                }

                const ticket = {
                  source: 'UNDEFINED_SOURCE',
                  type: 'TICKET_TYPE_ORDER',
                  status: 'TICKET_STATUS_OPEN',
                  title: title,
                  description: description,
                  mediaIds: listTemplatePdf,
                  listAttachment: docList,
                  ownerId: element.ownerId,
                  priority: this.priority,
                  startDate: this.executeFrom,
                  dueDate: this.executeUntil,
                  id: '',
                  key: this.ticketKeys[index],
                  assignee: currentAssigne,
                  creator: currentUser,
                  onSiteContact: null,
                  onSiteContacts: currentContactPersonOnSite,
                  additionalData: [
                    ...(this.onSiteContactType ? [{ key: 'onSiteContactType', value: this.onSiteContactType }] : []),
                    ...(recipientCompanyId ? [{ key: 'recipientCompanyId', value: recipientCompanyId }] : []),
                    ...this.getDeviceOrderNotesAdditionalData()
                  ],
                  watchers: [],
                  implementationLead: {},
                  correspondences: correspondences,
                  externalTicketWatchers: externalTicketWatchers,
                  links: link,
                  references: this.getReferences().filter(x => x.id === element.id || !x.id.startsWith('device')) || [],
                  involvedPersons: [],
                  ...draftCosts
                }
                this.listTickets.push(ticket)
              })
            }
          }, 100)
        } else {
          let ownerId = this.accountId
          const description = this.getTicketDescription() || ''
          const title = this.$refs.orderDataTab.title || ''
          if (renderIds.length > 0) {
            renderIds.forEach((element, index) => {
              const template = this.contractTemplate.find(x => x.id === array[index].contract)
              const contractName = template.title
              const downloadName = contractName + '_' + this.selectedDevices.map(x => this.$t('message.onBoarding.devices.typeValues.' + x.type)).toString().replaceAll(',', '_')

              const item = {
                id: Math.random().toString(36).substr(2, 9),
                filename: downloadName + '.pdf',
                mimeType: 'application/pdf',
                renderId: element,
                url: '',
                loading: false,
                blob: null
              }
              docList.push(item)
            })
          }

          const ticket = {
            source: 'UNDEFINED_SOURCE',
            type: 'TICKET_TYPE_ORDER',
            status: 'TICKET_STATUS_OPEN',
            title: title,
            description: description,
            mediaIds: listTemplatePdf,
            listAttachment: docList,
            ownerId: this.selectedDevices.length > 0 ? this.selectedDevices[0]?.ownerId : this.selectedObjects.length > 0 ? (this.selectedObjects[0]?.owner ? this.selectedObjects[0].owner.id : this.selectedObjects[0].ownerId) : ownerId,
            priority: this.priority,
            startDate: this.executeFrom,
            dueDate: this.executeUntil,
            id: '',
            key: this.ticketKeys[0],
            assignee: currentAssigne,
            creator: currentUser,
            watchers: [],
            implementationLead: {},
            correspondences: correspondences,
            onSiteContact: null,
            onSiteContacts: currentContactPersonOnSite,
            additionalData: [
              ...(this.onSiteContactType ? [{ key: 'onSiteContactType', value: this.onSiteContactType }] : []),
              ...(recipientCompanyId ? [{ key: 'recipientCompanyId', value: recipientCompanyId }] : []),
              ...this.getDeviceOrderNotesAdditionalData()
            ],
            externalTicketWatchers: externalTicketWatchers,
            links: link,
            references: this.getReferences(),
            involvedPersons: [],
            ...draftCosts
          }
          this.listTickets.push(ticket)
        }
        if (draft) {
          this.$nextTick(() => {
            this.submitTickets(true)
          })
        }
      })
        .catch(e => {
          Sentry.captureException(e)
          console.error(e)
          Vue.toasted.show('Eigentümer konnten nicht geladen werden', { type: 'error' })
        })
    },
    getRoomReferences() {
      const fromReferences = (this.references || []).filter(ref => ref.id?.startsWith('room_'))
      if (fromReferences.length > 0) {
        return fromReferences
      }
      if (this.draftTicket?.references?.length > 0) {
        return this.draftTicket.references.filter(ref => ref.id?.startsWith('room_'))
      }
      return []
    },
    usesRoomTicketReferences() {
      return this.getRoomReferences().length > 0 &&
        (this.source === 'room' || !!this.draftTicketId || this.source === 'draftOrder')
    },
    mergeUniqueReferences(primary, additional) {
      const merged = [...primary]
      const ids = new Set(primary.map(ref => ref.id))
      additional.forEach(ref => {
        if (!ids.has(ref.id)) {
          merged.push(ref)
          ids.add(ref.id)
        }
      })
      return merged
    },
    getReferences() {
      const roomRefs = this.getRoomReferences()
      if (this.source === 'mergeOrders') {
        let list = this.references.filter(x => !x.id.startsWith('device'))
        list = list.concat(this.getReferencesDevices())
        return this.mergeUniqueReferences(list, roomRefs)
      }
      if (this.usesRoomTicketReferences()) {
        if (this.selectedDevices.length > 0) {
          return this.mergeUniqueReferences(this.getReferencesDevices(), roomRefs)
        }
        return roomRefs
      }
      if (this.selectedDevices.length > 0) {
        return this.getReferencesDevices()
      }
      if (this.selectedObjects.length > 0) {
        return this.getReferencesObjects()
      }
      return this.references
    },
    getReferencesDevices() {
      const references = []
      this.selectedDevices.forEach(device => {
        if (device.referenceId.startsWith('obj')) {
          const relatedIds = [device.referenceId]
          const object = this.objects.find(x => x.id === device.referenceId)
          const building = this.buildings.find(x => x.id === object.buildingId)
          if (building) {
            relatedIds.push(building.id)
            const property = this.properties.find(x => x.buildingIds.includes(building.id))
            if (property) {
              relatedIds.push(property.id)
            }
          }
          references.push({
            id: device.id,
            relatedIds: relatedIds
          })
        } else if (device.referenceId.startsWith('building')) {
          const relatedIds = [device.referenceId]
          const property = this.properties.find(x => x.buildingIds.includes(device.referenceId))
          if (property) {
            relatedIds.push(property.id)
          }
          references.push({
            id: device.id,
            relatedIds: relatedIds
          })
        } else if (device.referenceId.startsWith('prop')) {
          references.push({
            id: device.id,
            relatedIds: [device.referenceId]
          })
        }
      })
      return references
    },
    getReferencesObjects() {
      const references = []
      this.selectedObjects.forEach(obj => {
        if (obj.id.startsWith('tenancy')) {
          const relatedIds = []
          const tenancies = this.tenancies.concat(this.tenanciesAlgolia)
          const tenancy = tenancies.find(x => x.id === obj.id)
          if (tenancy) {
            const objectId = tenancy.obj ? tenancy.obj.id : tenancy.objectId
            const object = this.objects.concat(this.objectsAlgolia).find(x => x.id === objectId)
            if (object) {
              relatedIds.push(object.id)
              const building = this.buildings.concat(this.buildingsAlgolia).find(x => x.id === object.buildingId)
              if (building) {
                relatedIds.push(building.id)
                const property = this.properties.concat(this.propertiesAlgolia).find(x => x.buildingIds.includes(building.id))
                if (property) {
                  relatedIds.push(property.id)
                }
              }
              references.push({
                id: tenancy.id,
                relatedIds: relatedIds
              })
            }
          }
        } else if (obj.id.startsWith('condominium')) {
          const relatedIds = []
          const condoOwners = this.condoOwners.concat(this.condoOwnersAlgolia)
          const condoOwner = condoOwners.find(x => x.id === obj.id)
          if (condoOwner) {
            const objectId = condoOwner.obj ? condoOwner.obj.id : condoOwner.objectId
            const object = this.objects.concat(this.objectsAlgolia).find(x => x.id === objectId)
            if (object) {
              relatedIds.push(object.id)
              const building = this.buildings.concat(this.buildingsAlgolia).find(x => x.id === object.buildingId)
              if (building) {
                relatedIds.push(building.id)
                const property = this.properties.concat(this.propertiesAlgolia).find(x => x.buildingIds.includes(building.id))
                if (property) {
                  relatedIds.push(property.id)
                }
              }
              references.push({
                id: condoOwner.id,
                relatedIds: relatedIds
              })
            }
          }
        } else if (obj.id.startsWith('obj')) {
          const relatedIds = []
          const objects = this.objects.concat(this.objectsAlgolia)
          const object = objects.find(x => x.id === obj.id)
          const buildings = this.buildings.concat(this.buildingsAlgolia)
          const building = buildings.find(x => x.id === object.buildingId)
          if (building) {
            relatedIds.push(building.id)
            const properties = this.properties.concat(this.propertiesAlgolia)
            const property = properties.find(x => x.buildingIds.includes(building.id))
            if (property) {
              relatedIds.push(property.id)
            }
          }
          references.push({
            id: obj.id,
            relatedIds: relatedIds
          })
        } else if (obj.id.startsWith('building')) {
          const relatedIds = []
          const properties = this.properties.concat(this.propertiesAlgolia)
          const property = properties.find(x => x.buildingIds.includes(obj.id))
          if (property) {
            relatedIds.push(property.id)
          }
          references.push({
            id: obj.id,
            relatedIds: relatedIds
          })
        } else if (obj.id.startsWith('prop')) {
          references.push({
            id: obj.id,
            relatedIds: []
          })
        }
      })
      return references
    },
    translatePlaceholders(message: string = ''): Promise<string> {
      return new Promise((resolve) => {
        const list = []
        let description = ''
        const references = this.getReferences()
        if (references.length > 0) {
          references.forEach(element => {
            if (element.id.startsWith('obj') || element.relatedIds.some(item => item.startsWith('obj'))) {
              let objectId = element.id.startsWith('obj') ? element.id : element.relatedIds.find(item => item.startsWith('obj'))
              list.push({
                contactId: this.recipientContact ? ('id' in this.recipientContact ? this.recipientContact.id : this.recipientContact.objectID) : '',
                objectId: objectId
              })
            }
            if (element.id.startsWith('building')) {
              list.push({
                contactId: this.recipientContact ? ('id' in this.recipientContact ? this.recipientContact.id : this.recipientContact.objectID) : '',
                buildingId: element.id
              })
            }
            if (element.id.startsWith('prop')) {
              list.push({
                contactId: this.recipientContact ? ('id' in this.recipientContact ? this.recipientContact.id : this.recipientContact.objectID) : '',
                propertyId: element.id
              })
            }

            if (element.id.startsWith('room') && this.rooms.length > 0) {
              const room = this.rooms.find(x => x.id === element.id)
              if (room?.referenceId.startsWith('building')) {
                list.push({
                  contactId: this.recipientContact ? ('id' in this.recipientContact ? this.recipientContact.id : this.recipientContact.objectID) : '',
                  buildingId: room.referenceId
                })
              }
              if (room?.referenceId.startsWith('obj')) {
                list.push({
                  contactId: this.recipientContact ? ('id' in this.recipientContact ? this.recipientContact.id : this.recipientContact.objectID) : '',
                  objectId: room.referenceId
                })
              }
            }
          })
        } else if (this.recipientContact) {
          list.push({
            contactId: this.recipientContact ? ('id' in this.recipientContact ? this.recipientContact.id : this.recipientContact.objectID) : ''
          })
        }
        if (list.length > 0) {
          MessageApi.bulkRenderText(list, message, '').then(response => {
            if (response?.entries.length > 0) {
              response?.entries.forEach(element => {
                if (element?.text) {
                  description += element.text + '\n'
                }
              })
            }
            resolve(description)
          }).catch(e => {
            console.log(e)
            resolve(message)
          })
        } else {
          resolve(message)
        }
      })
    },
    getTicketDescription(deviceId: string = ''): string {
      let description = ''
      const orderDetails = (this.orderDescription || '').trim()
      if (orderDetails !== '') {
        description += '\n' + this.$t('message.createOrder.orderDetails') + ':' + '\n' + orderDetails
      }
      if (this.source !== 'mergeOrders') {
        const useDeviceDescription = this.getDevicesForDescription(deviceId).length > 0 || this.source === 'device' || ((this.draftTicketId || ['ticket', 'draftOrder'].includes(this.source)) && this.deviceIds)
        if (useDeviceDescription) {
          description += this.getDeviceDescription(deviceId)
          description += this.getConnectedLocationDescription(description, this.getDevicesForDescription(deviceId))
        } else {
          if (this.source === 'property' || this.source === 'email' || ((this.draftTicketId || ['ticket', 'draftOrder'].includes(this.source)) && this.propertyIds)) {
            description += this.getPropertyDescription()
          }
          if (this.source === 'building' || this.source === 'email' || ((this.draftTicketId || ['ticket', 'draftOrder'].includes(this.source)) && this.buildingIds)) {
            description += this.getBuildingDescription()
          }
          if (this.source === 'object' || this.source === 'email' || this.source === 'condominium' || this.source === 'tenancy' || this.source === 'condoOwner' || ((this.draftTicketId || ['ticket', 'draftOrder'].includes(this.source)) && (this.tenancyIds || this.condoOwnerIds || this.objectIds))) {
            description += this.getObjectDescription()
          }
          if (this.source === 'room' || ((this.draftTicketId || ['ticket', 'mergeOrders', 'draftOrder'].includes(this.source)) && this.roomIds)) {
            description += this.getRoomDescription()?.description
          }
        }
      } else if (this.orderDescription === '') {
        description = this.descriptionMergeOrders
        // if (this.selectedDevices.length > 0 || this.deviceIds) {
        //   description += this.getDeviceDescription(deviceId)
        // }
        // if (this.propertyIds) {
        //   description += this.getPropertyDescription()
        // }
        // if (this.buildingIds) {
        //   description += this.getBuildingDescription()
        // }
        // if (this.tenancyIds || this.condoOwnerIds || this.objectIds) {
        //   description += this.getObjectDescription()
        // }
        // if (this.roomIds) {
        //   description += this.getRoomDescription()?.description
        // }
      }
      return description
    },
    getTicketTitle(deviceId: string): string {
      let title = ''
      const data = this.properties.concat(this.propertiesAlgolia).concat(this.buildings).concat(this.buildingsAlgolia).concat(this.objects).concat(this.objectsAlgolia)
      if (this.createSeparateTicket) {
        const device = this.selectedDevices.find(x => x.id === deviceId)
        if (device.type !== '' && device.type !== 'DEVICE_TYPE_UNDEFINED') {
          title = (title === '' ? title : title + ' - ') + this.$t('message.onBoarding.devices.typeValues.' + device.type)
        }
        if (device.brand !== '') {
          title = (title !== '' ? title + ', ' : '') + device.brand
        }
        if (title === '') {
          const ref = data.find(x => x.id === device.referenceId)
          let name = ref?.name || ''
          if (ref && name === '') {
            if (ref.id.startsWith('obj')) {
              name = data.find(x => x.id === ref.buildingId)?.name || ''
            }
          }
          title = name
        }
      } else {
        if (this.selectedDevices.length > 1) {
          const entity = this.selectedDevices.filter(x => x.referenceId.startsWith('prop')).length > 0 ? this.selectedDevices.filter(x => x.referenceId.startsWith('prop')) : this.selectedDevices.filter(x => x.referenceId.startsWith('building')).length > 0 ? this.selectedDevices.filter(x => x.referenceId.startsWith('building')) : this.selectedDevices.filter(x => x.referenceId.startsWith('obj'))
          const titleRef = entity.length > 0 ? data.find(x => x.id === entity[0].referenceId)?.name : ''
          title = this.$t('message.ticketTitle', { name: titleRef })
        } else if (this.selectedDevices.length === 1) {
          if (this.selectedDevices[0].type !== '' && this.selectedDevices[0].type !== 'DEVICE_TYPE_UNDEFINED') {
            title = this.$t('message.onBoarding.devices.typeValues.' + this.selectedDevices[0].type)
          }
          if (title === '') {
            const ref = data.find(x => x.id === this.selectedDevices[0].referenceId)
            let name = ref?.name || ''
            if (ref && name === '') {
              if (ref.id.startsWith('obj')) {
                name = data.find(x => x.id === ref.buildingId)?.name || ''
              }
            }
            title = name
          }
        }
      }
      return title
    },
    getDeviceDescription(deviceId: string = ''): string {
      let description = ''
      const data = this.properties.concat(this.propertiesAlgolia).concat(this.buildings).concat(this.buildingsAlgolia).concat(this.objects).concat(this.objectsAlgolia)
      const list = this.getDevicesForDescription(deviceId)
      list.forEach(device => {
        description = description + '\n'
        if (data.length > 0) {
          if (device.referenceId.startsWith('prop')) {
            const property = data.find(x => x.id === device.referenceId) || null
            if (property) {
              data.forEach(item => {
                if (property.id === item.id) {
                  description = description + '\n' + this.$t('message.createOrder.property') + ' ' + property.name
                  description = description + '\n' + this.$t('message.createOrder.city') + ' ' + property.address.zip + ' ' + property.address.city + '\n'
                }
              })
            }
          }
          if (device.referenceId.startsWith('building')) {
            data.forEach(item => {
              if (device.referenceId === item.id) {
                description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(item.address)
                description = description + '\n' + this.$t('message.createOrder.location') + ' ' + item.name
                if (device.roomId !== '') {
                  const room = this.rooms.find(x => x.id === device.roomId)
                  if (room) {
                    description = description + (room?.name ? ', ' + room.name : '')
                  }
                }
              }
            })
          }
          if (device.referenceId.startsWith('obj')) {
            data.forEach(item => {
              if (device.referenceId === item.id) {
                const objectNumber = this.getObjectLocationNumber(item)
                description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(item.address)
                description = description + '\n' + this.$t('message.createOrder.location') + ' ' + item.name + (objectNumber ? ', ' + objectNumber : '') + ', ' + this.$t('message.onBoarding.buildings.objects.category.' + item.category.toLowerCase()) + (item.characteristics?.floor !== -10000 ? ' - ' + this.formatFloor(item.characteristics?.floor) : '')
                if (device.roomId !== '') {
                  const room = this.rooms.find(x => x.id === device.roomId)
                  if (room) {
                    description = description + ', ' + room.name
                  }
                }
              }
            })
          }
        }
        if (device.type !== '' && device.type !== 'DEVICE_TYPE_UNDEFINED') {
          description = description + '\n' + this.$t('message.createOrder.componentDevice') + ' ' + this.$t('message.onBoarding.devices.typeValues.' + device.type)
        }
        if (device.manufacturer !== '') {
          description = description + '\n' + this.$t('message.createOrder.manufacturer') + ' ' + device.manufacturer
        }
        if (device.brand !== '') {
          description = description + '\n' + this.$t('message.createOrder.brand') + ' ' + device.brand
        }
        if (device.model !== '') {
          description = description + '\n' + this.$t('message.createOrder.model') + ' ' + device.model
        }
        if (device.serialNumber !== '') {
          description = description + '\n' + this.$t('message.createOrder.serialNumber') + ' ' + device.serialNumber
        }
        if (device.assetNumberFm !== '') {
          description = description + '\n' + this.$t('message.createOrder.assetNumberFm') + ' ' + device.assetNumberFm
        }
        const deviceOrderNote = this.getDeviceOrderNote(device)
        if (deviceOrderNote) {
          description = description + '\n\n' + this.$t('message.createOrder.orderDescription') + ':' + '\n' + deviceOrderNote + '\n'
        }
      })
      return description + '\n'
    },
    getConnectedObjectIds(): string[] {
      const ids = new Set<string>()
      this.normalizeIds(this.objectIds).forEach(id => ids.add(id))
      this.selectedObjects.forEach(object => {
        const id = object.id || object.objectID
        if (id) {
          ids.add(id)
        }
      })
      this.references.forEach(ref => {
        if (ref.id?.startsWith('obj')) {
          ids.add(ref.id)
        }
        ref.relatedIds?.forEach(relatedId => {
          if (relatedId?.startsWith('obj')) {
            ids.add(relatedId)
          }
        })
      })
      this.selectedDevices.forEach(device => {
        if (device.referenceId?.startsWith('obj')) {
          ids.add(device.referenceId)
        }
      })
      return [...ids]
    },
    getConnectedPropertyIds(): string[] {
      const ids = new Set<string>()
      this.normalizeIds(this.propertyIds).forEach(id => ids.add(id))
      this.references.forEach(ref => {
        if (ref.id?.startsWith('prop')) {
          ids.add(ref.id)
        }
        ref.relatedIds?.forEach(relatedId => {
          if (relatedId?.startsWith('prop')) {
            ids.add(relatedId)
          }
        })
      })
      return [...ids]
    },
    getConnectedBuildingIds(): string[] {
      const ids = new Set<string>()
      this.normalizeIds(this.buildingIds).forEach(id => ids.add(id))
      this.references.forEach(ref => {
        if (ref.id?.startsWith('building')) {
          ids.add(ref.id)
        }
        ref.relatedIds?.forEach(relatedId => {
          if (relatedId?.startsWith('building')) {
            ids.add(relatedId)
          }
        })
      })
      return [...ids]
    },
    getConnectedLocationDescription(existingDescription: string = '', devicesInDescription: any[] = []): string {
      let description = ''
      const appendIfMissing = (block: string) => {
        const trimmedBlock = block.trim()
        if (trimmedBlock && !existingDescription.includes(trimmedBlock) && !description.includes(trimmedBlock)) {
          description += block
        }
      }

      // Only exclude buildings and properties that have already been covered by getDeviceDescription
      // for the current set of devices in devicesInDescription.
      // Don't use all selectedDevices here—otherwise, addresses for unrelated devices could be wrongly omitted
      const deviceCoveredBuildingIds = new Set<string>(
        devicesInDescription
          .filter((d: any) => d.referenceId?.startsWith('building'))
          .map((d: any) => d.referenceId)
      )
      const deviceCoveredPropertyIds = new Set<string>(
        devicesInDescription
          .filter((d: any) => d.referenceId?.startsWith('prop'))
          .map((d: any) => d.referenceId)
      )
      // Properties are also covered when any of their buildings is already described by a device.
      this.properties.concat(this.propertiesAlgolia).forEach((property: any) => {
        if (property.buildingIds?.some((bId: string) => deviceCoveredBuildingIds.has(bId))) {
          deviceCoveredPropertyIds.add(property.id)
        }
      })

      if (this.getConnectedPropertyIds().length > 0) {
        appendIfMissing(this.getPropertyDescription(deviceCoveredPropertyIds))
      }
      if (this.getConnectedBuildingIds().length > 0) {
        appendIfMissing(this.getBuildingDescription(deviceCoveredBuildingIds))
      }
      if (this.getConnectedObjectIds().length > 0) {
        appendIfMissing(this.getObjectDescription())
      }
      return description
    },
    getPropertyDescription(excludeIds: Set<string> = new Set()): string {
      let description = ''
      const connectedPropertyIds = this.getConnectedPropertyIds()
      const data = this.properties.concat(this.propertiesAlgolia)
      data.forEach((property: any) => {
        if (excludeIds.has(property.id)) {
          return
        }
        if (connectedPropertyIds.includes(property.id) || this.selectedObjects.some((x: any) => x.id === property.id || x.objectID === property.id)) {
          description = description + '\n' + this.$t('message.createOrder.property') + ' ' + property.name
          description = description + '\n' + this.$t('message.createOrder.city') + ' ' + property.address.zip + ' ' + property.address.city + '\n'
        }
      })
      return description
    },
    getBuildingDescription(excludeIds: Set<string> = new Set()): string {
      let description = ''
      const connectedBuildingIds = this.getConnectedBuildingIds()
      const data = this.buildings.concat(this.buildingsAlgolia)
      const covered = this.getRoomCoveredParentIds()
      data.forEach((building: any) => {
        if (covered.has(building.id) || excludeIds.has(building.id)) {
          return
        }
        if (connectedBuildingIds.includes(building.id) || this.selectedObjects.some((x: any) => x.id === building.id || x.objectID === building.id)) {
          description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(building.address) + '\n'
        }
      })
      return description
    },
    getObjectDescription(): string {
      let description = ''
      const connectedObjectIds = this.getConnectedObjectIds()
      const data = this.objects.concat(this.objectsAlgolia)
      const covered = this.getRoomCoveredParentIds()
      data.forEach(object => {
        if (covered.has(object.id)) {
          return
        }
        const objectNumber = this.getObjectLocationNumber(object)
        const tenancy = this.source === 'tenancy' ? (this.tenancies.find(x => x.objectId === object.id) || null) : null
        const condoOwner = this.source === 'condoOwner' ? (this.condoOwners.find(x => x.objectId === object.id) || null) : null
          if (connectedObjectIds.includes(object.id) || tenancy || condoOwner) {
            if (tenancy) {
              let contactNames = ''
              tenancy.tenant.contactIds.forEach((element, index) => {
                const contact = this.combinedContacts.find(x => x.id === element)
                if (contact) {
                  contactNames = contactNames + (index > 0 ? ', ' : '') + contact.name
                }
              })
              if (contactNames !== '') {
                description = description + '\n' + contactNames
              }
            } else if (condoOwner) {
              let contactNames = ''
              condoOwner.owner.contactIds.forEach((element, index) => {
                const contact = this.combinedContacts.find(x => x.id === element)
                if (contact) {
                  contactNames = contactNames + (index > 0 ? ', ' : '') + contact.name
                }
              })
              if (contactNames !== '') {
                description = description + '\n' + contactNames
              }
            }
            const categoryKey = (object.category || 'UNDEFINED').toLowerCase()
            description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(object.address)
            description = description + '\n' + this.$t('message.createOrder.location') + ' ' + object.name + (objectNumber ? ', ' + objectNumber : '') + ', ' + this.$t('message.onBoarding.buildings.objects.category.' + categoryKey) + (object.characteristics?.floor !== -10000 ? ' - ' + this.formatFloor(object.characteristics?.floor) : '') + '\n'
          }
      })
      return description
    },
    // Reference ids (object/building) that already have at least one selected room,
    // so their address is rendered by getRoomDescription and must not be repeated by
    // getObjectDescription/getBuildingDescription.
    getRoomCoveredParentIds(): Set<string> {
      const covered = new Set<string>()
      this.rooms.forEach(room => {
        if (this.roomIds.includes(room.id) && room.referenceId) {
          covered.add(room.referenceId)
        }
      })
      return covered
    },
    getRoomDescription(): { description: string } {
      let description = ''
      // Group selected rooms by their parent so the address is printed once per parent,
      // followed by one location line per room.
      const parentOrder: string[] = []
      const roomsByParent: Record<string, ObjectroomRoom[]> = {}
      this.rooms.forEach(room => {
        if (this.roomIds.includes(room.id) && room.referenceId) {
          if (!roomsByParent[room.referenceId]) {
            roomsByParent[room.referenceId] = []
            parentOrder.push(room.referenceId)
          }
          roomsByParent[room.referenceId].push(room)
        }
      })
      parentOrder.forEach(referenceId => {
        const parentRooms = roomsByParent[referenceId]
        if (referenceId.startsWith('building')) {
          const building = this.buildings.find(x => x.id === referenceId) || null
          if (building) {
            description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(building.address)
            parentRooms.forEach(room => {
              description = description + '\n' + this.$t('message.createOrder.location') + ' ' + building.name + (room?.name ? ', ' + room.name : '')
            })
          }
        } else if (referenceId.startsWith('obj')) {
          const object = this.objects.find(x => x.id === referenceId) || null
          if (object) {
            const objectNumber = object.number
            const locationBase = object.name + (objectNumber ? ', ' + objectNumber : '') + ', ' + this.$t('message.onBoarding.buildings.objects.category.' + (object.category || 'UNDEFINED').toLowerCase()) + (object.characteristics?.floor !== -10000 ? ' - ' + this.formatFloor(object.characteristics?.floor) : '')
            description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(object.address)
            parentRooms.forEach(room => {
              description = description + '\n' + this.$t('message.createOrder.location') + ' ' + locationBase + (room?.name ? ', ' + room.name : '')
            })
          }
        }
      })
      return { description: description }
    },
    convertAddress(address: Record<string, string> | null): string {
      if (!address) {
        return '-'
      }

      const addr = (address.careOf ? address.careOf + '\n' : '') +
        (address.street ? ' ' + address.street + (address.streetNumber ? ' ' + address.streetNumber : '') : '') +
        (address.mailbox ? ' ' + address.mailbox + '\n ' : '') +
        (address.zip ? ' ' + address.country + '-' : '') +
        (address.zip ? address.zip + ' ' : '') +
        (address.city ? address.city : '')
      if (addr) {
        return addr
      } else {
        return ''
      }
    },
    submitTickets(draft: boolean = false) {
      if (this.listTickets.length > 0) {
        this.confirmCancellationProcess = true
        this.loading = true
        this.disableBtns = true
        const promisesMedia = []
        const promisesNewTickets = []
        let listBlobs = []
        let renderId = []
        if (this.mediasList.length > 0) {
          const ids = this.mediasList.map(x => x.id)
          MediaApi.setPublicFlag(ids, true).catch(error => {
            console.log(error)
          })
        }
        if (this.listTickets[0].listAttachment.length > 0) {
          renderId = this.listTickets[0].listAttachment.filter(x => x.blob === null).map(x => x.renderId)
          listBlobs = this.listTickets[0].listAttachment.filter(x => x.blob !== null)
          if (listBlobs.length > 0) {
            listBlobs.forEach(item => {
              const formData = new FormData()
              const fileName = this.editFileName(item.filename)
              formData.append('file', item.blob, fileName)
              formData.append('filename', item.filename)
              formData.append('public', 'true')
              promisesMedia.push(MediaApi.uploadMedia(formData))
            })
          }
          if (renderId.length > 0) {
            const recheckDocs = setInterval(() => {
              ObjectApi.listRenderStatus(renderId)
                .then(response => {
                  renderId = response.status.filter(x => !x.finished && !x.failed).map(x => x.renderId)
                  const stillInProgress = response.status.filter(function (v) {
                    return v.finished === false && v.failed === false
                  })
                  if (stillInProgress.length === 0) {
                    const isFinished = response.status.every(function (v) {
                      return v.finished !== false || v.failed !== false
                    })
                    if (isFinished) {
                      const onlyFinished = response.status.filter(x => x.finished)
                      const onlyFailed = response.status.filter(x => x.failed)
                      if (onlyFailed.length > 0) {
                        Vue.toasted.show(this.$t('message.savingErrors.renderingContractError') + onlyFailed.map(x => x.renderId).toString() + ' ' + onlyFailed.map(x => x.errorMessage).toString(), { type: 'error' })
                      }
                      if (onlyFinished.length > 0) {
                        onlyFinished.forEach((e, index) => {
                          ObjectApi.getRenderFile(e.renderId)
                            .then(element => {
                              // decode base64 string
                              const binary = window.atob(element.contract)
                              const len = binary.length
                              const buffer = new ArrayBuffer(len)
                              const view = new Uint8Array(buffer)
                              for (let i = 0; i < len; i++) {
                                view[i] = binary.charCodeAt(i)
                              }
                              let blob = new Blob([view], { type: 'application/pdf' })

                              const template = this.listTickets[0].listAttachment.find(x => x.renderId === e.renderId)

                              const formData = new FormData()
                              const fileName = this.editFileName(template.filename)
                              formData.append('file', blob, fileName)
                              formData.append('filename', template.filename)
                              formData.append('public', 'true')
                              promisesMedia.push(MediaApi.uploadMedia(formData))
                              if (renderId.length === 0 && index === onlyFinished.length - 1) {
                                clearInterval(recheckDocs)
                                const mediaIds = []
                                Promise.all(promisesMedia)
                                  .then(response => {
                                    for (const media of response) {
                                      mediaIds.push(media.id)
                                    }
                                    this.listTickets.forEach(element => {
                                      if (draft) {
                                        element.status = 'TICKET_STATUS_DRAFT'
                                      }
                                      element.mediaIds = mediaIds.concat(element.mediaIds)
                                      promisesNewTickets.push(this.submitOrUpdateTicket(element))
                                    })
                                    if (this.source === 'mergeOrders' && this.ticketsToMerge.length > 0) {
                                      const index = this.ticketsToMerge.findIndex(x => x.id === this.mainTicketForMerge)
                                      if (index !== -1) {
                                        const ids = this.ticketsToMerge.filter(y => y.id !== this.mainTicketForMerge).map(x => x.id)
                                        this.ticketsToMerge[index].links = this.ticketsToMerge[index].links.filter(x => !(ids).includes(x.ticketId))
                                      }
                                      this.ticketsToMerge.forEach((ticket) => {
                                        if (ticket.id !== this.mainTicketForMerge) {
                                          ticket.status = 'TICKET_STATUS_CLOSED'
                                          ticket.links = ticket.links.filter(x => x.ticketId !== this.mainTicketForMerge)
                                          promisesNewTickets.push(TicketApi.deleteTicket(ticket.id))
                                        }
                                      })
                                    }
                                    Promise.all(promisesNewTickets).then((response) => {
                                      if (this.source === 'email' && this.emailsData && response.length > 0) {
                                        const refs = []
                                        response.forEach(element => {
                                          if (element.ticket?.id) {
                                            refs.push(element.ticket.id)
                                          }
                                        })
                                        const email = JSON.parse(this.emailsData)
                                        const id = email.id
                                        MessageApi.setReferenceIds([id], refs.concat(email.referenceIds))
                                          .catch(error => {
                                            console.log(error)
                                          })
                                      }
                                      const ids = this.listTickets[0].listAttachment.map(x => x.renderId)
                                      ObjectApi.deleteRenderFiles(ids)
                                        .catch(exeption => {
                                          console.error(exeption)
                                        })
                                      this.redirectOnFinish('ticket', draft)
                                    }).catch(error => {
                                      console.log(error)
                                      this.handleErrorOnFinish(error)
                                    })
                                  })
                              }
                            })
                        })
                      } else if (onlyFailed.length > 0 && onlyFinished.length === 0) {
                        clearInterval(recheckDocs)
                        const mediaIds = []
                        Promise.all(promisesMedia)
                          .then(response => {
                            for (const media of response) {
                              mediaIds.push(media.id)
                            }
                            this.listTickets.forEach(element => {
                              if (draft) {
                                element.status = 'TICKET_STATUS_DRAFT'
                              }
                              element.mediaIds = mediaIds.concat(element.mediaIds)
                              promisesNewTickets.push(this.submitOrUpdateTicket(element))
                            })
                            if (this.source === 'mergeOrders' && this.ticketsToMerge.length > 0) {
                              const index = this.ticketsToMerge.findIndex(x => x.id === this.mainTicketForMerge)
                              if (index !== -1) {
                                const ids = this.ticketsToMerge.filter(y => y.id !== this.mainTicketForMerge).map(x => x.id)
                                this.ticketsToMerge[index].links = this.ticketsToMerge[index].links.filter(x => !(ids).includes(x.ticketId))
                              }
                              this.ticketsToMerge.forEach((ticket) => {
                                if (ticket.id !== this.mainTicketForMerge) {
                                  ticket.status = 'TICKET_STATUS_CLOSED'
                                  ticket.links = ticket.links.filter(x => x.ticketId !== this.mainTicketForMerge)
                                  promisesNewTickets.push(TicketApi.deleteTicket(ticket.id))
                                }
                              })
                            }
                            Promise.all(promisesNewTickets).then((response) => {
                              if (this.source === 'email' && this.emailsData && response.length > 0) {
                                const refs = []
                                response.forEach(element => {
                                  if (element.ticket?.id) {
                                    refs.push(element.ticket.id)
                                  }
                                })
                                const email = JSON.parse(this.emailsData)
                                const id = email.id
                                MessageApi.setReferenceIds([id], refs.concat(email.referenceIds))
                                  .catch(error => {
                                    console.log(error)
                                  })
                              }
                              const ids = this.listTickets[0].listAttachment.map(x => x.renderId)
                              ObjectApi.deleteRenderFiles(ids)
                                .catch(exeption => {
                                  console.error(exeption)
                                })
                              this.redirectOnFinish('ticket', draft)
                            }).catch(error => {
                              console.log(error)
                              this.handleErrorOnFinish(error)
                            })
                          })
                      }
                    }
                  }
                })
            }, 3000)
          } else if (renderId.length === 0 && listBlobs.length > 0) {
            const mediaIds = []
            Promise.all(promisesMedia)
              .then(response => {
                for (const media of response) {
                  mediaIds.push(media.id)
                }
                this.listTickets.forEach(element => {
                  if (draft) {
                    element.status = 'TICKET_STATUS_DRAFT'
                  }
                  element.mediaIds = mediaIds.concat(element.mediaIds)
                  promisesNewTickets.push(this.submitOrUpdateTicket(element))
                })
                if (this.source === 'mergeOrders' && this.ticketsToMerge.length > 0) {
                  const index = this.ticketsToMerge.findIndex(x => x.id === this.mainTicketForMerge)
                  if (index !== -1) {
                    const ids = this.ticketsToMerge.filter(y => y.id !== this.mainTicketForMerge).map(x => x.id)
                    this.ticketsToMerge[index].links = this.ticketsToMerge[index].links.filter(x => !(ids).includes(x.ticketId))
                  }
                  this.ticketsToMerge.forEach((ticket) => {
                    if (ticket.id !== this.mainTicketForMerge) {
                      ticket.status = 'TICKET_STATUS_CLOSED'
                      ticket.links = ticket.links.filter(x => x.ticketId !== this.mainTicketForMerge)
                      promisesNewTickets.push(TicketApi.deleteTicket(ticket.id))
                    }
                  })
                }
                Promise.all(promisesNewTickets).then((response) => {
                  if (this.source === 'email' && this.emailsData && response.length > 0) {
                    const refs = []
                    response.forEach(element => {
                      if (element.ticket?.id) {
                        refs.push(element.ticket.id)
                      }
                    })
                    const email = JSON.parse(this.emailsData)
                    const id = email.id
                    MessageApi.setReferenceIds([id], refs.concat(email.referenceIds))
                      .catch(error => {
                        console.log(error)
                      })
                  }
                  const ids = this.listTickets[0].listAttachment.map(x => x.renderId)
                  ObjectApi.deleteRenderFiles(ids)
                    .catch(exeption => {
                      console.error(exeption)
                    })
                  this.redirectOnFinish('ticket', draft)
                }).catch(error => {
                  console.log(error)
                  this.handleErrorOnFinish(error)
                })
              })
          }
        } else {
          const promises = []
          this.listTickets.forEach(element => {
            if (draft) {
              element.status = 'TICKET_STATUS_DRAFT'
            }
            promises.push(this.submitOrUpdateTicket(element))
          })
          if (this.source === 'mergeOrders' && this.ticketsToMerge.length > 0) {
            const index = this.ticketsToMerge.findIndex(x => x.id === this.mainTicketForMerge)
            if (index !== -1) {
              const ids = this.ticketsToMerge.filter(y => y.id !== this.mainTicketForMerge).map(x => x.id)
              this.ticketsToMerge[index].links = this.ticketsToMerge[index].links.filter(x => !(ids).includes(x.ticketId))
            }
            this.ticketsToMerge.forEach((ticket) => {
              if (ticket.id !== this.mainTicketForMerge) {
                ticket.status = 'TICKET_STATUS_CLOSED'
                ticket.links = ticket.links.filter(x => x.ticketId !== this.mainTicketForMerge)
                promises.push(TicketApi.deleteTicket(ticket.id))
              }
            })
          }
          Promise.all(promises).then((response) => {
            if (this.source === 'email' && this.emailsData && response.length > 0) {
              const refs = []
              response.forEach(element => {
                if (element.ticket?.id) {
                  refs.push(element.ticket.id)
                }
              })
              const email = JSON.parse(this.emailsData)
              const id = email.id
              MessageApi.setReferenceIds([id], refs.concat(email.referenceIds))
                .then(() => {
                  this.redirectOnFinish('ticket', draft)
                })
            } else {
              this.redirectOnFinish('ticket', draft)
            }
          }).catch(error => {
            console.log(error)
            this.handleErrorOnFinish(error)
          })
        }
      }
    },
    buildTicketPayloadForSubmit(element: TicketTicket): TicketTicket {
      const deviceRef = element.references?.find(ref => ref.id?.startsWith('device_'))
      element.description = this.getTicketDescription(deviceRef?.id || '') || ''
      element.additionalData = this.mergeTicketAdditionalData(element.additionalData || [])
      return element
    },
    submitOrUpdateTicket(element: TicketTicket) {
      const ticket = this.buildTicketPayloadForSubmit(element)
      if (this.draftTicketId && this.draftTicket) {
        ticket.id = this.draftTicket.id
        ticket.links = this.mergeTicketLinks(ticket.links || [], this.draftTicket.links || [])
        return TicketApi.updateTicket(ticket)
      }
      return TicketApi.newTicket(ticket)
    },
    mergeTicketLinks(primaryLinks: TicketTicketLink[] = [], existingLinks: TicketTicketLink[] = []) {
      const mergedLinks = [...primaryLinks]
      existingLinks.forEach(existingLink => {
        const alreadyIncluded = mergedLinks.some(link => link.ticketId === existingLink.ticketId && link.type === existingLink.type)
        if (!alreadyIncluded) {
          mergedLinks.push(existingLink)
        }
      })
      return mergedLinks
    },
    updateAttachAuto(value: boolean) {
      this.attachAutomatically = value
    },
    updateStatusContacts(value: Contact[]) {
      this.selectedContactsUpdatesStatus = value
    },
    async loadReferences() {
      this.references = []
      const sourceMatchesRef = ['ticket', 'mergeOrders', 'draftOrder'].includes(this.source) || !!this.draftTicketId
      const splitIds = (ids) => (typeof ids === 'string' ? ids.split(',').filter(Boolean) : [])
      const objectIds = splitIds(this.objectIds)
      const buildingIds = splitIds(this.buildingIds)
      const propertyIds = splitIds(this.propertyIds)
      const scopedDraftSource = (this.source === 'draftOrder' || !!this.draftTicketId) && this.source !== 'mergeOrders'
      const scopedReferenceType = scopedDraftSource
        ? (objectIds.length > 0 ? 'object' : buildingIds.length > 0 ? 'building' : propertyIds.length > 0 ? 'property' : '')
        : ''
      const buildingByObjectCache = new Map()
      const propertyByBuildingCache = new Map()
      const upsertBuilding = (building) => {
        if (building && !this.buildings.some(x => x.id === building.id)) {
          this.buildings.push(building)
        }
      }
      const upsertProperty = (property) => {
        if (property && !this.properties.some(x => x.id === property.id)) {
          this.properties.push(property)
        }
      }
      const resolveBuildingByObjectId = async (objectId) => {
        if (!objectId) {
          return null
        }
        if (buildingByObjectCache.has(objectId)) {
          return buildingByObjectCache.get(objectId)
        }
        const knownBuilding = this.buildings.find(x => x.objectIds?.includes(objectId))
        if (knownBuilding) {
          buildingByObjectCache.set(objectId, knownBuilding)
          return knownBuilding
        }
        const response = await ObjectApi.getBuildingByObjectId(objectId)
        const building = response.building || null
        if (building) {
          upsertBuilding(building)
        }
        buildingByObjectCache.set(objectId, building)
        return building
      }
      const resolvePropertyByBuildingId = async (buildingId) => {
        if (!buildingId) {
          return null
        }
        if (propertyByBuildingCache.has(buildingId)) {
          return propertyByBuildingCache.get(buildingId)
        }
        const knownProperty = this.properties.find(x => x.buildingIds?.includes(buildingId))
        if (knownProperty) {
          propertyByBuildingCache.set(buildingId, knownProperty)
          return knownProperty
        }
        const response = await ObjectApi.getPropertiesByBuildingIds([buildingId])
        const property = response.properties?.length > 0 ? response.properties[0] : null
        if (property) {
          upsertProperty(property)
        }
        propertyByBuildingCache.set(buildingId, property)
        return property
      }
      const buildRelatedFromObject = async (objectId) => {
        const relatedIds = []
        if (!objectId) {
          return relatedIds
        }
        const building = await resolveBuildingByObjectId(objectId)
        if (scopedReferenceType === 'object') {
          return [objectId]
        }
        relatedIds.push(objectId)
        if (building) {
          relatedIds.push(building.id)
          const property = await resolvePropertyByBuildingId(building.id)
          if (property) {
            relatedIds.push(property.id)
          }
        }
        return relatedIds
      }
      const buildRelatedFromBuilding = async (buildingId) => {
        const relatedIds = []
        if (!buildingId) {
          return relatedIds
        }
        if (scopedReferenceType === 'building') {
          return [buildingId]
        }
        relatedIds.push(buildingId)
        const property = await resolvePropertyByBuildingId(buildingId)
        if (property) {
          relatedIds.push(property.id)
        }
        return relatedIds
      }

      if (this.source === 'device' || (sourceMatchesRef && this.deviceIds !== '')) {
        const arrayDeviceIds = splitIds(this.deviceIds)
        for (const element of this.devices) {
          if (arrayDeviceIds.includes(element.id)) {
            if (element.referenceId.startsWith('obj_')) {
              const obj = {
                id: element.id,
                relatedIds: await buildRelatedFromObject(element.referenceId)
              }
              this.references.push(obj)
            } else if (element.referenceId.startsWith('building_')) {
              const obj = {
                id: element.id,
                relatedIds: await buildRelatedFromBuilding(element.referenceId)
              }
              this.references.push(obj)
            } else if (element.referenceId.startsWith('prop_')) {
              const obj = {
                id: element.id,
                relatedIds: [element.referenceId]
              }
              this.references.push(obj)
            }
          }
        }
      }

      if (this.source === 'object' || this.source === 'condominium' || (sourceMatchesRef && this.objectIds !== '' && (scopedReferenceType === '' || scopedReferenceType === 'object'))) {
        const arrayObjectIds = objectIds
        for (const element of this.objects) {
          if (arrayObjectIds.includes(element.id)) {
            const obj = {
              id: element.id,
              relatedIds: await buildRelatedFromObject(element.id)
            }
            this.references.push(obj)
          }
        }
      }

      if (this.source === 'building' || (sourceMatchesRef && this.buildingIds !== '' && (scopedReferenceType === '' || scopedReferenceType === 'building'))) {
        const arrayBuildingIds = buildingIds
        for (const element of this.buildings) {
          if (arrayBuildingIds.includes(element.id)) {
            const obj = {
              id: element.id,
              relatedIds: await buildRelatedFromBuilding(element.id)
            }
            this.references.push(obj)
          }
        }
      }

      if (this.source === 'property' || (sourceMatchesRef && this.propertyIds !== '' && (scopedReferenceType === '' || scopedReferenceType === 'property'))) {
        const arrayPropertyIds = propertyIds
        for (const element of this.properties) {
          if (arrayPropertyIds.includes(element.id)) {
            const obj = {
              id: element.id,
              relatedIds: [element.id]
            }
            this.references.push(obj)
          }
        }
      }

      if (this.source === 'tenancy' || this.source === 'condoOwner' || (sourceMatchesRef && (this.tenancyIds !== '' || this.condoOwnerIds !== ''))) {
        const list = sourceMatchesRef ? this.tenancies.concat(this.condoOwners) : this.source === 'tenancy' ? this.tenancies : this.condoOwners
        const arrayIds = sourceMatchesRef
          ? splitIds(this.tenancyIds).concat(splitIds(this.condoOwnerIds))
          : this.source === 'tenancy'
            ? splitIds(this.tenancyIds)
            : splitIds(this.condoOwnerIds)
        for (const element of list) {
          if (arrayIds.includes(element.id)) {
            const obj = {
              id: element.id,
              relatedIds: await buildRelatedFromObject(element.objectId)
            }
            this.references.push(obj)
          }
        }
      }

      if (this.source === 'room' || (sourceMatchesRef && this.roomIds !== '')) {
        const arrayRoomIds = splitIds(this.roomIds)
        for (const element of this.rooms) {
          if (arrayRoomIds.includes(element.id)) {
            const room = this.rooms.find(x => x.id === element.id)
            if (room && room.referenceId.startsWith('obj_')) {
              const obj = {
                id: room.id,
                relatedIds: await buildRelatedFromObject(room.referenceId)
              }
              this.references.push(obj)
            }
            if (room && room.referenceId.startsWith('building_')) {
              const obj = {
                id: room.id,
                relatedIds: await buildRelatedFromBuilding(room.referenceId)
              }
              this.references.push(obj)
            }
          }
        }
      }

      if (['ticket', 'mergeOrders'].includes(this.source) && this.contactIds !== '') {
        const arrayContactIds = this.contactIds.split(',')
        for (const element of this.contacts) {
          if (arrayContactIds.includes(element.id)) {
            const obj = {
              id: element.id,
              relatedIds: []
            }
            this.references.push(obj)
          }
        }
      }
      if (this.references.length === 0 && this.draftTicketId && this.draftTicket?.references?.length > 0) {
        this.references = this.draftTicket.references.map(ref => ({
          id: ref.id,
          relatedIds: ref.relatedIds || []
        }))
      }
      // remove duplicated items
      this.references = this.references.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
      if (this.source === 'device') {
        this.ticketTitle = this.getTicketTitle(this.deviceIds)
      }
      this.finishLoadingReferences = true
    },
    async loadDevices() {
      this.loading = true
      const deviceIds = typeof this.deviceIds === 'string' ? this.deviceIds.split(',') : this.deviceIds
      await ObjectApi.listDevicesByIds(deviceIds)
        .then(async response => {
          this.devices = response.devices
          // Check if we can only load objects and tenants by referenceId of devices
          const objectsIds = this.devices.filter(dev => dev.referenceId.startsWith('obj_')).map(dev => dev.referenceId) || []
          const buildingIds = this.devices.filter(dev => dev.referenceId.startsWith('building_')).map(dev => dev.referenceId) || []
          const propertyIds = this.devices.filter(dev => dev.referenceId.startsWith('prop_')).map(dev => dev.referenceId) || []
          const roomIds = this.devices.filter(dev => dev.roomId).map(dev => dev.roomId) || []
          if (buildingIds.length > 0) {
            // We need to load the building to get the objects and janitors because no device refers to an object
            await this.loadBuildings(buildingIds, objectsIds)
          }
          if (objectsIds.length > 0) {
            await this.loadObjects(objectsIds)
          }
          if (propertyIds) {
            await this.loadProperties(propertyIds)
          }
          if (roomIds) {
            await this.loadRooms(roomIds)
          }
          this.loading = false
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.device'), { type: 'error' })
          this.loading = false
        })
    },
    async loadBuildings(buildingIds: string[] | string, additionalObjectsIds: string[]) {
      try {
        const buildings = typeof buildingIds === 'string' ? buildingIds.split(',') : buildingIds
        const response = await ObjectApi.getBuildingsListByIds(buildings)
        const list = response.buildings.concat(this.buildings)
        this.buildings = list.length > 0 ? list.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i) : []
        this.ownersIds = this.removeDuplicatedIds(this.ownersIds.concat(response.buildings.map(x => x.ownerId)))
        const objectIds = response.buildings.flatMap(x => x.objectIds)
        await this.loadObjects(objectIds.concat(additionalObjectsIds))
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    async loadProperties(propertyIds: string[] | string = []) {
      try {
        const properties = typeof propertyIds === 'string' ? propertyIds.split(',') : propertyIds
        const response = await ObjectApi.getPropertyListByIds(properties)
        const list = response.properties.concat(this.properties)
        this.properties = list.length > 0 ? list.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i) : []
        this.ownersIds = this.removeDuplicatedIds(this.ownersIds.concat(response.properties.map(x => x.ownerId)))
        const buildingIds = response.properties.flatMap(x => x.buildingIds)
        await this.loadBuildings(buildingIds, [])
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    loadOwners() {
      const promises = []
      if (this.ownersIds.length > 0) {
        for (const id of this.ownersIds) {
          promises.push(UserApi.getAccount(id))
        }
        Promise.all(promises).then((result) => {
          const names = []
          result.forEach(response => {
            if (response.account?.company) {
              names.push(response.account?.company?.name)
            } else if (response.account?.individual) {
              const name = response.account?.individual?.firstName + ' ' + response.account?.individual?.lastName
              names.push(name)
            }
          })
          this.ownersList = names
        })
      }
    },
    async loadRoomsByReference(referenceIds: string[]) {
      try {
        const responseRooms = await RoomApi.getRoomsByReferenceIds(referenceIds)
        const rooms = this.rooms.concat(responseRooms.rooms)
        this.rooms = rooms.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
      }
    },
    async loadObjects(objectIds: string[]) {
      try {
        this.finishLoadingReferences = false
        const response = await ObjectApi.listObjectsByIds(objectIds.filter(id => id))
        // concat objects list and remove duplicated items
        const list = response.objects.concat(this.objects)
        this.objects = list.length > 0 ? list.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i) : []

        this.ownersIds = this.removeDuplicatedIds(this.ownersIds.concat(response.objects.map(x => x.ownerId)))
        const rentalObjectIds = this.objects.filter(x => x.condominiumOwnership === false).map(x => x.id)
        const condominiumIds = this.objects.filter(x => x.condominiumOwnership === true).map(x => x.id)
        if (rentalObjectIds.length > 0) {
          await this.loadTenantContacts(rentalObjectIds)
        } else if (condominiumIds.length > 0) {
          await this.loadCondominiumContacts(condominiumIds)
        }
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
      }
    },
    removeDuplicatedIds(items: string[]): string[] {
      const uniqueIds = []
      return items.filter(element => {
        const isDuplicate = uniqueIds.filter(y => y === element)
        if (isDuplicate.length === 0) {
          uniqueIds.push(element)
          return true
        }
        return false
      })
    },
    /**
     * Building may store a contact-person id while resolve returns the company row; keep those rows.
     */
    contactMatchesBuildingContactIds(contact: Contact, buildingContactIds: string[]): boolean {
      if (!buildingContactIds?.length) {
        return false
      }
      const idSet = new Set(buildingContactIds)
      const c = contact as Contact & { objectID?: string }
      const primaryId = c.id || c.objectID
      if (primaryId && idSet.has(primaryId)) {
        return true
      }
      const persons = contact.contactPersons
      if (Array.isArray(persons)) {
        for (const p of persons) {
          const person = p as { id?: string; objectID?: string }
          const pid = person.id || person.objectID
          if (pid && idSet.has(pid)) {
            return true
          }
        }
      }
      return false
    },
    /**
     * Buildings from Algolia/manual link may omit `contacts` or include null entries; guard before filtering.
     */
    collectBuildingContactIdsByType(contactType: string): string[] {
      const ids: string[] = []
      for (const building of this.buildings.concat(this.buildingsAlgolia)) {
        if (!building) {
          continue
        }
        const contacts = Array.isArray(building.contacts) ? building.contacts : []
        for (const c of contacts) {
          if (c && c.type === contactType && c.id) {
            ids.push(c.id)
          }
        }
      }
      return [...new Set(ids)]
    },
    async loadJanitors() {
      try {
        const janitorIds = this.collectBuildingContactIdsByType('BUILDING_CONTACT_TYPE_JANITOR')
        if (janitorIds.length === 0) {
          this.janitorContacts = []
          return
        }
        const response = await ContactApi.contactResolve(janitorIds)
        let list = this.janitorContacts.concat(response.persons).concat(response.companies)
        list = [...new Map(list.map(item => [item.id, item])).values()]
        this.janitorContacts = list.length > 0 ? list.filter(x => this.contactMatchesBuildingContactIds(x, janitorIds)) : []
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.janitors'), { type: 'error' })
      }
    },
    async loadCraftsmans() {
      try {
        const craftsmanIds = this.collectBuildingContactIdsByType('BUILDING_CONTACT_TYPE_CRAFTSMAN')
        if (craftsmanIds.length === 0) {
          this.craftsmanContacts = []
          return
        }
        const response = await ContactApi.contactResolve(craftsmanIds)
        let list = this.craftsmanContacts.concat(response.persons).concat(response.companies)
        list = [...new Map(list.map(item => [item.id, item])).values()]
        this.craftsmanContacts = list.length > 0 ? list.filter(x => this.contactMatchesBuildingContactIds(x, craftsmanIds)) : []
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.janitors'), { type: 'error' })
      }
    },
    async loadServiceProviders() {
      try {
        const serviceProviderIds = this.collectBuildingContactIdsByType('BUILDING_CONTACT_TYPE_SERVICE_PROVIDER')
        if (serviceProviderIds.length === 0) {
          this.serviceProviderContacts = []
          return
        }
        const response = await ContactApi.contactResolve(serviceProviderIds)
        let list = this.serviceProviderContacts.concat(response.persons).concat(response.companies)
        list = [...new Map(list.map(item => [item.id, item])).values()]
        this.serviceProviderContacts = list.length > 0 ? list.filter(x => this.contactMatchesBuildingContactIds(x, serviceProviderIds)) : []
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.janitors'), { type: 'error' })
      }
    },
    async loadDelegates() {
      try {
        const delegateIds = this.collectBuildingContactIdsByType('BUILDING_CONTACT_TYPE_DELEGATE')
        if (delegateIds.length === 0) {
          this.delegateContacts = []
          return
        }
        const response = await ContactApi.contactResolve(delegateIds)
        let list = this.delegateContacts.concat(response.persons).concat(response.companies)
        list = [...new Map(list.map(item => [item.id, item])).values()]
        this.delegateContacts = list.length > 0 ? list.filter(x => this.contactMatchesBuildingContactIds(x, delegateIds)) : []
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.delegates'), { type: 'error' })
      }
    },
    async loadTenantContacts(objectIds: string[]) {
      let tenantContactIds = []
      try {
        const tenancyResponse = await ObjectApi.getTenancies(objectIds)
        const today = new Date()
        const list = this.tenancies.concat(tenancyResponse.tenancies.filter(x => !x.vacancy && (!x.endingAt || new Date(this.$options.filters.objectToDateInput(x.startingAt)).getTime() >= today.getTime() || new Date(this.$options.filters.objectToDateInput(x.endingAt)).getTime() >= today.getTime())))
        this.tenancies = [...new Set(list.map(a => a.id))].map(id => {
          return list.find(a => a.id === id)
        })
        tenantContactIds = this.tenancies.map(t => t?.tenant?.contactIds || []).flat()
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.tenancy'), { type: 'error' })
      }

      if (tenantContactIds && tenantContactIds.length > 0) {
        try {
          const contactResponse = await ContactApi.contactResolve(tenantContactIds)
          const list = [...contactResponse.persons, ...contactResponse.companies]
          this.tenantContacts = list.filter(c => tenantContactIds.includes(c.id))
          // this.combinedContacts = this.combinedContacts.concat(this.tenantContacts)
        } catch (e) {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        }
      }
    },
    async loadCondominiumContacts(objectIds: string[]) {
      let condoOwnerContactIds = []
      try {
        const condominiumResponse = await ObjectApi.getCondominiumOwnership(objectIds)
        const today = new Date()
        const list = this.condoOwners.concat(condominiumResponse.condominiums.filter(x => !x.endingAt || new Date(this.$options.filters.objectToDateInput(x.startingAt)).getTime() >= today.getTime() || new Date(this.$options.filters.objectToDateInput(x.endingAt)).getTime() >= today.getTime()))
        this.condoOwners = [...new Set(list.map(a => a.id))].map(id => {
          return list.find(a => a.id === id)
        })
        condoOwnerContactIds = this.condoOwners.map(t => t?.owner?.contactIds || []).flat()
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.tenancy'), { type: 'error' })
      }

      if (condoOwnerContactIds && condoOwnerContactIds.length > 0) {
        try {
          const contactResponse = await ContactApi.contactResolve(condoOwnerContactIds)
          const list = [...contactResponse.persons, ...contactResponse.companies]
          this.condoOwnerContacts = list.filter(c => condoOwnerContactIds.includes(c.id))
          // this.combinedContacts = this.combinedContacts.concat(this.condoOwnerContacts)
        } catch (e) {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        }
      }
    },
    async loadContacts(ids: string[] = []) {
      try {
        const contactIds = this.contactIds.split(',').concat(ids)
        const contactResponse = await ContactApi.contactResolve(contactIds)
        this.contacts = [...contactResponse.persons, ...contactResponse.companies]
        // this.loadReferences()
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    async loadTenancies(tenancyIds: string[]) {
      try {
        const response = await ObjectApi.getTenanciesByIds(tenancyIds)
        this.tenancies = response.tenancies
        const objectIds = response.tenancies.map(x => x.objectId)
        await this.loadObjects(objectIds)
        await this.getDevicesByRefId(objectIds)
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    async loadCondoOwners(condoOwnersIds: string[]) {
      try {
        const response = await ObjectApi.getCondominiumByIds(condoOwnersIds)
        this.condoOwners = response.condominiums
        const objectIds = response.condominiums.map(x => x.objectId)
        await this.loadObjects(objectIds)
        await this.getDevicesByRefId(objectIds)
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    async loadRooms(roomIds: string[]) {
      try {
        const response = await RoomApi.listById(roomIds)
        const mergedRooms = this.rooms.concat(response.rooms.length > 0 ? response.rooms : [])
        this.rooms = this.filterDuplicateItemsById(mergedRooms)
        const roomIdSet = new Set(roomIds || [])
        const roomsToProcess = this.rooms.filter(x => roomIdSet.has(x.id))
        const objectReferenceIds = [...new Set(roomsToProcess
          .map(x => x.referenceId)
          .filter(id => id && id.startsWith('obj')))]
        const buildingReferenceIds = [...new Set(roomsToProcess
          .map(x => x.referenceId)
          .filter(id => id && id.startsWith('building')))]
        const missingObjectIds = objectReferenceIds.filter(id => !this.objects.some(x => x.id === id))
        const missingBuildingIds = buildingReferenceIds.filter(id => !this.buildings.some(x => x.id === id))
        if (missingObjectIds.length > 0) {
          await this.loadObjects(missingObjectIds)
        }
        if (missingBuildingIds.length > 0) {
          await this.loadBuildings(missingBuildingIds, [])
        }
        if (this.source === 'room') {
          this.selectedObjects = this.objects.filter(x => objectReferenceIds.includes(x.id))
          this.selectedBuildings = this.buildings.filter(x => buildingReferenceIds.includes(x.id))
        }
        const references = [...new Set(roomsToProcess.map(x => x.referenceId).filter(Boolean))]
        if (references && references.length > 0) {
          const deviceResponse = await ObjectApi.getDevicesByReferenceIds(references)
          const list = (deviceResponse?.devices || []).concat(this.devices)
          const devices = this.filterDuplicateItemsById(list)
          if (this.source === 'room') {
            this.devices = devices.filter(x => x.roomId && roomIds.includes(x.roomId))
          } else {
            this.devices = devices
          }
        }
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    getObjectName(objectId: string): string {
      const obj = this.objects.find(e => e.id === objectId)
      return obj ? obj.name : ''
    },
    getDevicesForDescription(deviceId: string = ''): HandoverDevice[] {
      if (deviceId !== '') {
        const device = this.selectedDevices.find(x => x.id === deviceId) ||
          this.devices.find(x => x.id === deviceId)
        return device ? [device] : []
      }
      if (this.selectedDevices.length > 0) {
        return this.selectedDevices
      }
      const notedDeviceIds = this.devicesForApi.filter(x => (x.note || '').trim()).map(x => x.id)
      return notedDeviceIds.length > 0 ? this.devices.filter(d => notedDeviceIds.includes(d.id)) : []
    },
    getDeviceOrderNote(device: HandoverDevice): string {
      const apiEntry = this.devicesForApi.find(x => x.id === device.id)
      const orderDescription = (device as HandoverDevice & { orderDescription?: string }).orderDescription
      return (apiEntry?.note || orderDescription || '').trim()
    },
    getDeviceOrderNotesAdditionalData() {
      const notes = this.devicesForApi
        .filter(x => (x.note || '').trim())
        .map(x => ({ deviceId: x.id, note: x.note.trim() }))
      if (notes.length === 0) {
        return []
      }
      return [{ key: 'deviceOrderNotes', value: JSON.stringify(notes) }]
    },
    loadDeviceOrderNotesFromDraft(ticket) {
      const notesEntry = ticket?.additionalData?.find(d => d.key === 'deviceOrderNotes')
      if (!notesEntry?.value) {
        return
      }
      try {
        const notes = JSON.parse(notesEntry.value)
        if (!Array.isArray(notes)) {
          return
        }
        notes.forEach(entry => {
          if (entry?.deviceId && entry?.note) {
            this.noteChanged(entry.deviceId, entry.note)
          }
        })
      } catch (e) {
        console.log(e)
      }
    },
    mergeTicketAdditionalData(existingAdditionalData = []) {
      const merged = [...existingAdditionalData.filter(d => d.key !== 'deviceOrderNotes')]
      return merged.concat(this.getDeviceOrderNotesAdditionalData())
    },
    noteChanged(id: string, note: string) {
      let apiEntry = this.devicesForApi.find(x => x.id === id)
      if (!apiEntry) {
        apiEntry = { id, note: '' }
        this.devicesForApi.push(apiEntry)
      }
      apiEntry.note = note
      const selectedDevice = this.selectedDevices.find(x => x.id === id)
      if (selectedDevice) {
        Vue.set(selectedDevice as HandoverDevice & { orderDescription?: string }, 'orderDescription', note)
      }
      const device = this.selectedDevices.find(x => x.id === id)
      if (device) {
        device.orderDescription = note
      }
    },
    syncDevicesForApi() {
      const previousNotes = new Map(this.devicesForApi.map(d => [d.id, d.note || '']))
      this.devicesForApi = this.devices.map(device => ({
        id: device.id,
        note: previousNotes.get(device.id) || ''
      }))
    },
    getOrderDraftAdditionalData(recipientCompanyId: string, deviceIds: string[] | null = null) {
      const additionalData = [
        ...(this.onSiteContactType ? [{ key: 'onSiteContactType', value: this.onSiteContactType }] : []),
        ...(recipientCompanyId ? [{ key: 'recipientCompanyId', value: recipientCompanyId }] : [])
      ]
      if (this.$refs?.orderDataTab?.sendStatusUpdates) {
        additionalData.push({ key: 'sendStatusUpdates', value: 'true' })
      }
      const targetDevices = deviceIds
        ? this.selectedDevices.filter(d => deviceIds.includes(d.id))
        : this.selectedDevices
      targetDevices.forEach(device => {
        const note = this.getDeviceOrderNote(device.id)
        if (note) {
          additionalData.push({ key: `deviceOrderDescription_${device.id}`, value: note })
        }
      })
      return additionalData
    },
    restoreDraftDeviceNotes() {
      if (!this.draftTicket) {
        return
      }
      const prefix = 'deviceOrderDescription_'
      const notesByDeviceId: Record<string, string> = {}
      this.draftTicket.additionalData?.forEach(entry => {
        if (entry.key?.startsWith(prefix) && entry.value) {
          notesByDeviceId[entry.key.slice(prefix.length)] = entry.value
        }
      })
      const applyNote = (deviceId: string, note: string) => {
        const trimmed = note.trim()
        if (!trimmed) {
          return
        }
        let entry = this.devicesForApi.find(x => x.id === deviceId)
        if (!entry) {
          entry = { id: deviceId, note: trimmed }
          this.devicesForApi.push(entry)
        } else {
          entry.note = trimmed
        }
        const device = this.selectedDevices.find(x => x.id === deviceId) ||
          this.devices.find(x => x.id === deviceId)
        if (device) {
          Vue.set(device as HandoverDevice & { orderDescription?: string }, 'orderDescription', trimmed)
        }
      }
      const storedKeys = Object.keys(notesByDeviceId)
      if (storedKeys.length > 0) {
        storedKeys.forEach(deviceId => applyNote(deviceId, notesByDeviceId[deviceId]))
        return
      }
      if (!this.draftTicket.description) {
        return
      }
      this.restoreDeviceNotesFromDescription(this.draftTicket.description)
    },
    restoreDeviceNotesFromDescription(description: string) {
      const label = this.$t('message.createOrder.orderDescription') + ':'
      const segments = description.split(label)
      if (segments.length < 2) {
        return
      }
      const noteSegments = segments.slice(1).map(segment => segment.trim().split('\n\n')[0].trim()).filter(Boolean)
      const devicesToRestore = this.selectedDevices.length > 0
        ? this.selectedDevices
        : this.devices
      devicesToRestore.forEach((device, index) => {
        if (noteSegments[index]) {
          const trimmed = noteSegments[index]
          let entry = this.devicesForApi.find(x => x.id === device.id)
          if (!entry) {
            entry = { id: device.id, note: trimmed }
            this.devicesForApi.push(entry)
          } else {
            entry.note = trimmed
          }
          Vue.set(device as HandoverDevice & { orderDescription?: string }, 'orderDescription', trimmed)
        }
      })
    },
    itemSelected(ids: string[], contracts: ContractTemplate[]) {
      this.contractTemplateSelected = ids
      this.contractTemplate = contracts
    },
    nextStep(props: { activeTabIndex: number; nextTab: () => void; prevTab: () => void }) {
      if (props.activeTabIndex === 0) {
        if (this.source !== 'email') {
          props.nextTab()
          this.scrollToTop()
        } else {
          if (this.selectedObjects.length < 1) {
            Vue.toasted.show(this.$tc('message.savingErrors.hasNoObject'), { type: 'error' })
          } else {
            props.nextTab()
            this.scrollToTop()
          }
        }
      } else if ((this.source !== 'email' && props.activeTabIndex === 1) || (this.source === 'email' && props.activeTabIndex === 2)) {
        if (this.recipientContact && !this.recipientContact.email) {
          Vue.toasted.show(this.$t('message.savingErrors.operatorHasNoEmail'), { type: 'error' })
        } else {
          props.nextTab()
          this.scrollToTop()
        }
      } else {
        props.nextTab()
        this.scrollToTop()
      }
    },
    finishProcess() {
      if (this.$refs.orderDataTab) {
        const isInvalid = this.$refs.orderDataTab.isInvalid()
        if (!isInvalid) {
          let contactObject = null
          if (this.onSiteContactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT' && (this.contact.firstName || this.contact.lastName || this.contact.email || this.contact.phone)) {
            contactObject = this.contact
          }
          this.loading = true
          const promises = []
          const array = []
          for (let j = 0; j < this.contractTemplateSelected.length; j++) {
            array.push({
              contract: this.contractTemplateSelected[j]
            })
            promises.push(
              ObjectApi.renderDevice(
                this.contractTemplateSelected[j],
                this.selectedEmployee,
                this.recipientContactId,
                this.devicesForApi,
                this.executeFrom,
                this.executeUntil,
                this.orderDescription,
                this.onSiteContactPersonIds.length > 0 ? this.onSiteContactPersonIds[0] : this.$refs.orderDataTab.selectedUser,
                contactObject,
                this.attachAutomatically
              )
            )
          }
          Promise.all(promises)
            .then(response => {
              const renderId = []
              response.forEach((element, index) => {
                const contract = this.contractTemplate.find(x => x.id === array[index].contract)
                const contractName = contract.title
                let downloadName = contractName + '_' + this.selectedDevices.map(x => this.$t('message.onBoarding.devices.typeValues.' + x.type)).toString().replaceAll(',', '_')
                const item = {
                  renderId: element.renderId + '_' + downloadName + '-/-' + contract.enforcePdf,
                  contract: contract
                }
                renderId.push(item)
              })
              const currentRenderIds = JSON.parse(localStorage.getItem('renderId') || '[]')
              const mergedRenderIds = Array.isArray(currentRenderIds) ? [...currentRenderIds, ...renderId] : renderId
              localStorage.setItem('renderId', JSON.stringify(mergedRenderIds))
              this.redirectOnFinish('createDocs')
            })
            .catch(e => {
              this.handleErrorOnFinish(e)
            })
        }
      }
    },
    handleErrorOnFinish(e: Error) {
      console.log(e)
      this.loading = false
      Vue.toasted.show(this.$t('message.savingErrors.createOrder'), { type: 'error' })
      const lastRoute = JSON.parse(localStorage.getItem('LS_ROUTE_KEY'))
      this.$router.push({
        name: lastRoute.name,
        params: lastRoute.params,
        query: lastRoute.query
      }, () => {
      })
    },
    redirectOnFinish(lastTask: string, draft: boolean = false) {
      setTimeout(() => {
        const lastRoute = JSON.parse(localStorage.getItem('LS_ROUTE_KEY'))
        if (lastTask === 'ticket') {
          if (draft) {
            Vue.toasted.show(this.$t('message.successMessages.orderDraftSaved'), { type: 'success' })
          } else {
            Vue.toasted.show(this.$t('message.successMessages.orderSent'), { type: 'success' })
          }
        } else if (lastTask === 'createDocs') {
          Vue.toasted.show(this.$t('message.successMessages.createOrder'), { type: 'success' })
        }
        this.$router.push({
          name: lastRoute.name,
          params: lastRoute.params,
          query: lastRoute.query
        }, () => {
        })
        this.loading = false
        this.disableBtns = false
      }, 2000)
    },
    backClicked(props) {
      props.prevTab()
      this.scrollToTop()
    },
    handleCheckboxesInsideTable(value: boolean) {
      if (this.$refs.deviceTable) {
        const allCheckboxesComponents = this.$refs.deviceTable.$children[1].$children
        this.handleCheckbox(allCheckboxesComponents, value)
      }
    },
    scrollToTop() {
      this.$nextTick(() => {
        const domRect = this.$el.querySelector('#wizard')
        domRect.scrollIntoView(true)
      })
    },
    recipientChanged(recipientId: string, recipient: Contact) {
      this.recipientContactId = recipientId
      this.recipientContact = recipient
    },
    orderDataUpdated(data: object) {
      Object.assign(this, data)
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.hideSteps.vue-form-wizard) {
  .wizard-header {
    display: none;
  }

  ul.wizard-nav-pills,
  .wizard-progress-with-circle {
    display: none !important;
  }
}

.scrollable-print-message {
  position: fixed;
  height: 100%;
  overflow-y: auto;
}
</style>
